import React, { useState, useEffect, useRef, useMemo } from "react";
import { selectedCompanyHasProfile } from "../utils/activityProfiles";
import {
  Alert,
  Form,
  Input,
  Button,
  Card,
  Typography,
  Select,
  Table,
  InputNumber,
  Space,
  message,
  Spin,
  Modal,
  Row,
  Col,
  Tag,
  Tabs,
  Tooltip,
  Switch,
  Radio,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  BarcodeOutlined,
  SaveOutlined,
  InfoCircleOutlined,
  ShoppingOutlined,
  SettingOutlined,
  TagOutlined,
  CopyOutlined,
  PrinterOutlined,
  RollbackOutlined,
  ArrowRightOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getProductionUnitById,
  createProductionUnit,
  updateProductionUnit,
  suggestProductionUnitPrice,
  getProducts,
  getRecipeSuggestions,
  getIngredientSuggestions,
  getMissingIngredientSuggestions,
  getProductionEquipment,
} from "./api";
import { useSelection } from "../SelectionContext";
import axios from "axios";
import Barcode from "react-barcode";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useAuth } from "../context/AuthContext";
import API_CONFIG from '../config/api.js';
import { getSelectedProductionTerminology } from "./productionTerminology";
import { compressImage } from "../utils/imageUrlHelper";
import { deduplicateEntities } from "../utils/entityUtils";
import RowActionsMenu from "../components/RowActionsMenu";
import { ProductionDisclosure, ProductionUnitImageInput, ProductionUnitSummary, useProductionImagePreview, useProductionWorkspaceStyle } from "./ProductionUnitWorkspace";
import { estimateProductionUnit } from "./productionUnitEstimate";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

const API_URL = API_CONFIG.BASE_URL;

// Styles globaux pour le formulaire
const formStyles = {
  card: {
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    borderRadius: "8px",
    marginBottom: "16px",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: "600",
    margin: "0 0 16px 0",
    padding: "0 0 8px 0",
    borderBottom: "1px solid #f0f0f0",
  },
  formSection: {
    padding: "0 0 16px 0",
  },
  actionButton: {
    borderRadius: "4px",
  },
};

// Fonction utilitaire pour formater les nombres
const formatNumber = (value) =>
  new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: 0,
    useGrouping: true,
  }).format(value);

const formatPercent = (value) =>
  new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    useGrouping: true,
  }).format(value);

const formatRecipeQuantity = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return "0";
  }

  return new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: Number.isInteger(numericValue) ? 0 : 4,
    useGrouping: true,
  }).format(numericValue);
};

const normalizePositiveNumber = (value, fallback = 0) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0
    ? numericValue
    : fallback;
};

const areCloseRecipeNumbers = (left, right, epsilon = 0.0001) =>
  Math.abs(Number(left || 0) - Number(right || 0)) <= epsilon;

const normalizePurchasePackagingProfiles = (profiles = []) =>
  (Array.isArray(profiles) ? profiles : [])
    .map((profile) => ({
      id: profile?.id ?? null,
      label: String(profile?.label || "").trim(),
      contained_quantity: normalizePositiveNumber(profile?.contained_quantity),
      reference_purchase_price: normalizePositiveNumber(
        profile?.reference_purchase_price
      ),
      is_default:
        profile?.is_default === true ||
        profile?.is_default === 1 ||
        profile?.is_default === "1",
    }))
    .filter(
      (profile) =>
        profile.label && normalizePositiveNumber(profile.contained_quantity) > 0
    );

const normalizeMaterialUsageProfiles = (profiles = []) =>
  (Array.isArray(profiles) ? profiles : [])
    .map((profile) => ({
      client_key:
        profile?.client_key ||
        `usage-${Date.now()}-${Math.round(Math.random() * 100000)}`,
      id: profile?.id ?? null,
      material_product_id:
        profile?.material_product_id ?? profile?.product_id ?? null,
      label: String(profile?.label || "").trim(),
      reference_output_quantity: normalizePositiveNumber(
        profile?.reference_output_quantity
      ),
      reference_output_unit_id: profile?.reference_output_unit_id ?? null,
      material_quantity: normalizePositiveNumber(profile?.material_quantity),
      material_unit_id: profile?.material_unit_id ?? null,
      loss_rate: normalizePositiveNumber(profile?.loss_rate, 0),
      yield_rate: normalizePositiveNumber(profile?.yield_rate, 0),
      notes: String(profile?.notes || "").trim(),
      source_production_unit_id:
        profile?.source_production_unit_id ?? profile?.production_unit_id ?? null,
      source_production_unit_name:
        profile?.source_production_unit_name || profile?.source_name || "",
      reference_output_unit_short_name:
        profile?.reference_output_unit_short_name || "",
      material_unit_short_name: profile?.material_unit_short_name || "",
      is_default:
        profile?.is_default === true ||
        profile?.is_default === 1 ||
        profile?.is_default === "1",
    }))
    .filter(
      (profile) =>
        profile.label &&
        normalizePositiveNumber(profile.reference_output_quantity) > 0 &&
        normalizePositiveNumber(profile.material_quantity) > 0
    );

const getDefaultPurchasePackagingProfile = (profiles = []) => {
  const normalizedProfiles = normalizePurchasePackagingProfiles(profiles);
  return (
    normalizedProfiles.find((profile) => profile.is_default) ||
    normalizedProfiles[0] ||
    null
  );
};

const getDefaultMaterialUsageProfile = (profiles = []) => {
  const normalizedProfiles = normalizeMaterialUsageProfiles(profiles);
  return (
    normalizedProfiles.find((profile) => profile.is_default) ||
    normalizedProfiles[0] ||
    null
  );
};

const calculateUsageProfileMaterialQuantity = (profile, outputQuantity) => {
  const referenceOutputQuantity = Number(profile?.reference_output_quantity);
  const materialQuantity = Number(profile?.material_quantity);
  const currentOutputQuantity = Number(outputQuantity);

  if (
    !Number.isFinite(referenceOutputQuantity) ||
    referenceOutputQuantity <= 0 ||
    !Number.isFinite(materialQuantity) ||
    materialQuantity <= 0 ||
    !Number.isFinite(currentOutputQuantity) ||
    currentOutputQuantity <= 0
  ) {
    return null;
  }

  const lossRate = Number(profile?.loss_rate) || 0;
  const yieldRate = Number(profile?.yield_rate) || 0;
  const lossMultiplier = 1 + Math.max(lossRate, 0) / 100;
  const yieldMultiplier = yieldRate > 0 ? 100 / yieldRate : 1;

  return Number(
    (
      (currentOutputQuantity / referenceOutputQuantity) *
      materialQuantity *
      lossMultiplier *
      yieldMultiplier
    ).toFixed(4)
  );
};

const PRODUCTION_KIND_FINISHED = "finished";
const PRODUCTION_KIND_SEMI_FINISHED = "semi_finished";
const BARCODE_SYMBOLOGIES = ["CODE128", "EAN13", "CODE39"];
const PRODUCTION_FORM_FIELD_TAB_MAP = {
  name: "1",
  slug: "1",
  warehouse: "1",
  category_id: "1",
  unit_id: "1",
  production_kind: "1",
  output_quantity: "2",
  production_packaging_label: "2",
  production_packaging_contained_quantity: "2",
  target_margin_rate: "2",
  stock_quantitiy_alert: "2",
  sales_price: "2",
  tax_id: "2",
  item_code: "4",
  barcode_symbology: "4",
};

const getProductionKindLabel = (kind) => {
  if (kind === PRODUCTION_KIND_SEMI_FINISHED) {
    return "Semi-fini";
  }

  return "Fini";
};

const normalizeBarcodeSymbology = (value) => {
  const normalized = String(value || "")
    .trim()
    .toUpperCase();
  return BARCODE_SYMBOLOGIES.includes(normalized) ? normalized : "CODE128";
};

const generateEan13Base = () =>
  Math.floor(Math.random() * 1000000000000)
    .toString()
    .padStart(12, "0");

const normalizeItemCodeForSymbology = (itemCode, symbology) => {
  const rawValue = String(itemCode || "").trim();

  if (!rawValue) {
    return "";
  }

  const normalizedSymbology = normalizeBarcodeSymbology(symbology);

  if (normalizedSymbology === "EAN13") {
    return rawValue.replace(/\D/g, "");
  }

  if (normalizedSymbology === "CODE39") {
    return rawValue.toUpperCase();
  }

  return rawValue;
};

const isValidItemCodeForSymbology = (itemCode, symbology) => {
  const value = String(itemCode || "").trim();
  const normalizedSymbology = normalizeBarcodeSymbology(symbology);

  if (!value) {
    return false;
  }

  if (normalizedSymbology === "EAN13") {
    return /^\d{12,13}$/.test(value);
  }

  if (normalizedSymbology === "CODE39") {
    return /^[0-9A-Z\-. $/+%]+$/.test(value);
  }

  return true;
};

const mergeProductsById = (...productLists) => {
  const map = new Map();

  productLists.flat().forEach((product) => {
    if (!product || !product.id) {
      return;
    }

    map.set(product.id, product);
  });

  return Array.from(map.values());
};

const areSameEntityId = (left, right) => {
  if (left === undefined || left === null || right === undefined || right === null) {
    return false;
  }

  return String(left) === String(right);
};

const getOptionText = (option) => {
  const nodes = React.Children.toArray(
    option?.children ?? option?.label ?? ""
  );

  return nodes
    .map((node) => {
      if (typeof node === "string" || typeof node === "number") {
        return node;
      }

      if (node && typeof node === "object" && node.props?.children) {
        return React.Children.toArray(node.props.children)
          .map((child) =>
            typeof child === "string" || typeof child === "number"
              ? child
              : ""
          )
          .join("");
      }

      return "";
    })
    .join("");
};

// Wrapper d'input pour la "Quantite produite" permettant de basculer entre
// une saisie en unite canonique (de stock) et une saisie en nombre de
// conditionnements de production (ex : "Marmite de 25 kg" contenant 22 plats).
// La valeur persistee dans le formulaire (`value`) reste toujours canonique,
// ce qui garantit qu'aucune logique metier aval (ratio recette, calcul cout,
// process production) n'est impactee.
const OutputQuantityInput = ({
  id,
  value,
  onChange,
  disabled,
  unitShortName,
  packagingLabel,
  containedQuantity,
  mode,
  onModeChange,
  onUserChange,
}) => {
  const packagingActive =
    Boolean((packagingLabel || "").trim()) &&
    Number(containedQuantity) > 0;
  const effectiveMode = packagingActive ? mode : "direct";
  const canonicalValue = Number.isFinite(Number(value)) ? Number(value) : null;
  const packagingValue =
    packagingActive && canonicalValue !== null
      ? canonicalValue / Number(containedQuantity)
      : null;
  const packagingQuantity = Number(containedQuantity) || 0;
  const packagingUnitLabel = (packagingLabel || "").trim() || "conditionnement";

  const handleModeChange = (event) => {
    const nextMode = event.target.value;
    if (onModeChange) {
      onModeChange(nextMode);
    }
  };

  const handleDirectChange = (nextValue) => {
    if (onUserChange) {
      onUserChange();
    }
    if (onChange) {
      onChange(nextValue);
    }
  };

  const handlePackagingChange = (nextPackagingValue) => {
    if (onUserChange) {
      onUserChange();
    }
    if (!onChange) {
      return;
    }
    if (
      nextPackagingValue === null ||
      nextPackagingValue === undefined ||
      nextPackagingValue === ""
    ) {
      onChange(null);
      return;
    }
    const numericPackaging = Number(nextPackagingValue);
    if (!Number.isFinite(numericPackaging) || packagingQuantity <= 0) {
      onChange(null);
      return;
    }
    onChange(numericPackaging * packagingQuantity);
  };

  return (
    <div>
      {packagingActive && (
        <Radio.Group
          size="small"
          value={effectiveMode}
          onChange={handleModeChange}
          disabled={disabled}
          style={{ marginBottom: 8 }}
        >
          <Radio.Button value="direct">Directe</Radio.Button>
          <Radio.Button value="packaging">Par conditionnement</Radio.Button>
        </Radio.Group>
      )}
      {effectiveMode === "packaging" && packagingActive ? (
        <>
          <InputNumber
            id={id}
            style={{ width: "100%" }}
            placeholder="Ex: 22"
            min={0.01}
            step={0.01}
            disabled={disabled}
            value={packagingValue}
            onChange={handlePackagingChange}
            addonAfter={packagingUnitLabel}
          />
          <Text type="secondary" className="text-xs">
            Soit {Number.isFinite(canonicalValue) ? canonicalValue : 0}{" "}
            {unitShortName || "unités"} (1 {packagingUnitLabel} ={" "}
            {packagingQuantity} {unitShortName || "unités"}).
          </Text>
        </>
      ) : (
        <InputNumber
          id={id}
          style={{ width: "100%" }}
          placeholder="Ex: 100"
          min={0.01}
          step={0.01}
          disabled={disabled}
          value={canonicalValue}
          onChange={handleDirectChange}
          addonAfter={unitShortName || "unités"}
        />
      )}
    </div>
  );
};

const ProductionUnitForm = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEditing = !!id;
  const { hasPermission, user } = useAuth();
  const { selectedCompany, selectedWarehouse } = useSelection();
  const productionTerminology = getSelectedProductionTerminology(
    user,
    selectedCompany
  );

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [variableCosts, setVariableCosts] = useState([]);
  const [equipmentLinks, setEquipmentLinks] = useState([]);
  const [availableEquipment, setAvailableEquipment] = useState([]);
  const [loadingEquipment, setLoadingEquipment] = useState(false);
  const [, setOutputs] = useState([]);
  const [priceSuggestion, setPriceSuggestion] = useState(null);
  const [suggestingPrice, setSuggestingPrice] = useState(false);
  const [modalVisible, setModalVisible] = useState(true);
  const [categories, setCategories] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [units, setUnits] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [taxes, setTaxes] = useState([]); // AJOUT: État pour les taxes
  const [barcodeGenerated, setBarcodeGenerated] = useState(false);
  const [currentBarcode, setCurrentBarcode] = useState(null);
  const [barcodeModalVisible, setBarcodeModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState(isEditing ? "2" : "1");
  const workspaceStyle = useProductionWorkspaceStyle();
  const [expandedSections, setExpandedSections] = useState({});
  const toggleSection = (key, expanded) =>
    setExpandedSections((current) => ({ ...current, [key]: expanded }));
  const revealSection = (key) => {
    setActiveTab("2");
    toggleSection(key, true);
    setTimeout(() => document.getElementById(`production-section-${key}`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" }), 160);
  };
  const [usageProfileModal, setUsageProfileModal] = useState(null);
  // Garde-fou recalibrage : Modal ouvert quand on tente d'enregistrer alors que
  // des intrants "stock" restent calibres pour une autre quantite produite.
  const [staleGuardOpen, setStaleGuardOpen] = useState(false);
  // Permet a handleSubmit de lire des intrants recalcules/conserves sans
  // dependre du timing de commit React (evite la course setState au save).
  const submitMaterialsOverrideRef = useRef(null);
  const productionAssistantMode =
    new URLSearchParams(location.search).get("assistant") ===
    "production-setup";
  const [recipeAssistantEnabled, setRecipeAssistantEnabled] = useState(
    !isEditing
  );
  // Moteur d'assistance recettes (local) : recettes similaires d'apres le nom.
  const [recipeNameSuggestions, setRecipeNameSuggestions] = useState([]);
  const [loadingRecipeSuggestions, setLoadingRecipeSuggestions] = useState(false);
  const recipeSuggestionTimerRef = useRef(null);
  // Moteur d'assistance recettes (local) : ingredients souvent ajoutes (manquants).
  const [missingIngredientSuggestions, setMissingIngredientSuggestions] = useState(
    []
  );
  const [loadingMissingIngredients, setLoadingMissingIngredients] = useState(
    false
  );
  const missingIngredientTimerRef = useRef(null);
  // Mode de saisie de la quantite produite :
  //   "direct"    -> saisie en unites canoniques (unite de stock)
  //   "packaging" -> saisie en nombre de conditionnements (ex : marmittes)
  // Passe automatiquement en "packaging" si l'utilisateur a renseigne un
  // conditionnement de production valide.
  const [outputQuantityInputMode, setOutputQuantityInputMode] = useState("direct");
  const outputQuantityManuallyEditedRef = useRef(false);
  const lastAutoSyncedOutputQuantityRef = useRef(null);
  const watchedProductionPackagingLabel = Form.useWatch(
    "production_packaging_label",
    form
  );
  const watchedProductionPackagingContainedQuantity = Form.useWatch(
    "production_packaging_contained_quantity",
    form
  );
  const watchedOutputQuantity = Form.useWatch("output_quantity", form);
  const watchedWarehouse = Form.useWatch("warehouse", form);
  const watchedRecipeName = Form.useWatch("name", form);
  const watchedUnit = Form.useWatch("unit_id", form);
  const watchedSalesPrice = Form.useWatch("sales_price", form);
  const watchedStockAlert = Form.useWatch("stock_quantitiy_alert", form);
  const watchedImage = Form.useWatch("image", form);
  const watchedCategory = Form.useWatch("category_id", form);
  const summaryImage = useProductionImagePreview(watchedImage);

  const markOutputQuantityAsManuallyEdited = () => {
    outputQuantityManuallyEditedRef.current = true;
  };

  // ---------------------------------------------------------------------------
  // RECALIBRAGE DYNAMIQUE DES INTRANTS "STOCK" (Qte directe)
  // Les intrants en mode "stock" portent une quantite absolue calibree pour la
  // quantite produite (`output_quantity`) au moment de leur pose. On memorise
  // cette base par-ligne (champs client jamais serialises) pour detecter une
  // divergence quand l'utilisateur change la quantite produite ensuite, puis
  // proposer un recalcul proportionnel (jamais silencieux).
  // ---------------------------------------------------------------------------
  const getCurrentOutputQuantity = () =>
    Number(form.getFieldValue("output_quantity")) || 0;

  const round2 = (value) => Number(Number(value).toFixed(2));

  // Recalcule la quantite "stock" d'un intrant pour la quantite produite `cur`.
  // Lignes moteur ("engine") : coefficient par-sortie fige (exact et stable sur
  // changements repetes). Lignes manuelles : regle de trois depuis la base.
  const recalcStockQuantity = (material, cur) => {
    if (
      material.quantity_source === "engine" &&
      Number(material.calibration_per_output) > 0
    ) {
      return round2(Number(material.calibration_per_output) * cur);
    }
    const base = Number(material.calibration_output_quantity);
    if (base > 0) {
      return round2((Number(material.quantity) * cur) / base);
    }
    return Number(material.quantity);
  };

  // Un intrant "stock" est desynchronise si sa base de calibrage differe
  // significativement de la quantite produite courante. Les lignes
  // usage_profile (auto-recalculees) et packaging (derivees du conditionnement)
  // sont exclues, de meme que les lignes placeholder sans produit.
  const isStaleStock = (material, cur) =>
    material.recipe_entry_mode === "stock" &&
    Boolean(material.product_id) &&
    Number(material.calibration_output_quantity) > 0 &&
    Number(material.quantity) > 0 &&
    Number.isFinite(cur) &&
    cur > 0 &&
    !areCloseRecipeNumbers(Number(material.calibration_output_quantity), cur);

  const findProductById = (productId) =>
    products.find((product) => areSameEntityId(product?.id, productId));

  const getMaterialPackagingProfiles = (material) =>
    normalizePurchasePackagingProfiles(material?.purchase_packaging_profiles);

  const getMaterialUsageProfiles = (material) =>
    normalizeMaterialUsageProfiles(material?.usage_profiles);

  const getSuggestedMaterialUsageProfiles = (material) =>
    normalizeMaterialUsageProfiles(material?.suggested_usage_profiles);

  const getSelectedPackagingProfile = (material) => {
    const profiles = getMaterialPackagingProfiles(material);
    if (!profiles.length) {
      return null;
    }

    return (
      profiles.find((profile) =>
        areSameEntityId(profile.id, material?.selected_packaging_profile_id)
      ) || getDefaultPurchasePackagingProfile(profiles)
    );
  };

  const getSelectedMaterialUsageProfile = (material) => {
    const profiles = getMaterialUsageProfiles(material);
    if (!profiles.length) {
      return null;
    }

    return (
      profiles.find(
        (profile) =>
          areSameEntityId(profile.id, material?.selected_usage_profile_id) ||
          profile.client_key === material?.selected_usage_profile_id
      ) || getDefaultMaterialUsageProfile(profiles)
    );
  };

  const syncMaterialUsageProfileSelection = (material, nextPartial = {}) => {
    const nextMaterial = { ...material, ...nextPartial };
    const profiles = getMaterialUsageProfiles(nextMaterial);
    const selectedProfile =
      profiles.find(
        (profile) =>
          areSameEntityId(profile.id, nextMaterial.selected_usage_profile_id) ||
          profile.client_key === nextMaterial.selected_usage_profile_id
      ) || getDefaultMaterialUsageProfile(profiles);
    const outputQuantity = Number(form.getFieldValue("output_quantity")) || 1;
    const calculatedQuantity = calculateUsageProfileMaterialQuantity(
      selectedProfile,
      outputQuantity
    );

    return {
      ...nextMaterial,
      usage_profiles: profiles,
      recipe_entry_mode: "usage_profile",
      selected_usage_profile_id:
        selectedProfile?.id ?? selectedProfile?.client_key ?? null,
      quantity:
        calculatedQuantity !== null
          ? calculatedQuantity
          : normalizePositiveNumber(nextMaterial.quantity, 1),
    };
  };

  const syncMaterialPackagingSelection = (
    material,
    nextPartial = {},
    options = {}
  ) => {
    const nextMaterial = { ...material, ...nextPartial };
    const profiles = getMaterialPackagingProfiles(nextMaterial);
    const canUsePackaging =
      String(nextMaterial.product_type || "").toLowerCase() === "raw" &&
      profiles.length > 0;

    nextMaterial.purchase_packaging_profiles = profiles;

    if (!canUsePackaging) {
      if (nextMaterial.recipe_entry_mode === "usage_profile") {
        return {
          ...nextMaterial,
          selected_packaging_profile_id: null,
          package_count: 1,
          quantity: normalizePositiveNumber(nextMaterial.quantity, 1),
        };
      }

      return {
        ...nextMaterial,
        recipe_entry_mode: "stock",
        selected_packaging_profile_id: null,
        selected_usage_profile_id: null,
        package_count: 1,
        quantity: normalizePositiveNumber(nextMaterial.quantity, 1),
      };
    }

    if (nextMaterial.recipe_entry_mode === "usage_profile") {
      return {
        ...nextMaterial,
        selected_packaging_profile_id: null,
        package_count: 1,
        quantity: normalizePositiveNumber(nextMaterial.quantity, 1),
      };
    }

    const nextMode =
      nextMaterial.recipe_entry_mode === "packaging" ? "packaging" : "stock";
    const selectedProfile =
      profiles.find((profile) =>
        areSameEntityId(profile.id, nextMaterial.selected_packaging_profile_id)
      ) || getDefaultPurchasePackagingProfile(profiles);
    const nextPackageCount = normalizePositiveNumber(
      nextMaterial.package_count,
      1
    );

    if (nextMode === "packaging" && selectedProfile) {
      return {
        ...nextMaterial,
        recipe_entry_mode: "packaging",
        selected_packaging_profile_id: selectedProfile.id,
        selected_usage_profile_id: null,
        package_count: nextPackageCount,
        quantity: Number(
          (nextPackageCount * selectedProfile.contained_quantity).toFixed(4)
        ),
      };
    }

    return {
      ...nextMaterial,
      recipe_entry_mode: "stock",
      selected_packaging_profile_id: selectedProfile?.id ?? null,
      selected_usage_profile_id: null,
      package_count: nextPackageCount,
      quantity: options.preserveQuantity
        ? nextMaterial.quantity
        : normalizePositiveNumber(nextMaterial.quantity, 1),
    };
  };

  const shouldAutoEnablePackagingForMaterial = (material, productData) => {
    return false;
  };

  const getProductionFormTabByField = (fieldPath) => {
    const fieldName = Array.isArray(fieldPath) ? fieldPath[0] : fieldPath;
    return PRODUCTION_FORM_FIELD_TAB_MAP[fieldName] || "1";
  };

  const focusProductionFormField = (fieldPath) => {
    const normalizedFieldPath = Array.isArray(fieldPath)
      ? fieldPath
      : [fieldPath];
    const fieldName = normalizedFieldPath[0];
    const targetTab = getProductionFormTabByField(normalizedFieldPath);
    if (fieldName === "slug") toggleSection("technical", true);
    if (String(fieldName).startsWith("production_packaging_")) toggleSection("packaging", true);
    if (["stock_quantitiy_alert", "sales_price", "target_margin_rate", "tax_id"].includes(fieldName)) {
      toggleSection("sales", true);
    }

    if (targetTab && targetTab !== activeTab) {
      setActiveTab(targetTab);
    }

    setTimeout(() => {
      form.scrollToField(normalizedFieldPath, {
        behavior: "smooth",
        block: "center",
      });

      const fieldInstance = form.getFieldInstance?.(normalizedFieldPath);
      if (fieldInstance?.focus) {
        fieldInstance.focus();
        return;
      }

      const domField =
        document.querySelector(`[id="${fieldName}"]`) ||
        document.querySelector(`[id$="_${fieldName}"]`) ||
        document.querySelector(`[name="${fieldName}"]`);

      const focusTarget =
        domField?.querySelector?.(
          "input,textarea,.ant-select-selection-search-input,.ant-input-number-input"
        ) || domField;

      if (focusTarget?.focus) {
        focusTarget.focus();
      }
    }, 160);
  };

  const focusInvalidMaterialInput = () => {
    setActiveTab("2");

    setTimeout(() => {
      const invalidMaterial = materials.find(
        (material) =>
          !material.product_id ||
          !Number.isFinite(Number(material.quantity)) ||
          Number(material.quantity) <= 0
      );

      if (invalidMaterial?.key !== undefined) {
        const row = document.querySelector(
          `tr[data-row-key="${invalidMaterial.key}"]`
        );
        const rowInput = row?.querySelector(
          ".ant-select-selection-search-input, .ant-input-number-input"
        );
        if (rowInput?.focus) {
          rowInput.focus();
          return;
        }
      }

      const fallbackInput = document.querySelector(
        ".ant-table-tbody .ant-select-selection-search-input, .ant-table-tbody .ant-input-number-input"
      );
      if (fallbackInput?.focus) {
        fallbackInput.focus();
      }
    }, 200);
  };

  const showFieldValidationError = (fieldPath, errorMessage, options = {}) => {
    if (fieldPath) {
      form.setFields([
        {
          name: Array.isArray(fieldPath) ? fieldPath : [fieldPath],
          errors: [errorMessage],
        },
      ]);
      focusProductionFormField(fieldPath);
    } else if (options.tabKey) {
      setActiveTab(options.tabKey);
    }

    message.error(errorMessage);
  };

  const handleProductionFormFinishFailed = (errorInfo) => {
    const firstError = errorInfo?.errorFields?.[0];
    const firstField = firstError?.name;
    const firstErrorMessage =
      firstError?.errors?.[0] ||
      "Veuillez corriger les informations du formulaire.";

    if (firstField?.length) {
      focusProductionFormField(firstField);
    }

    message.error(firstErrorMessage);
  };

  const handleProductionSubmitError = (rawError) => {
    const errorMessage =
      rawError?.error ||
      rawError?.details ||
      rawError?.response?.data?.error ||
      rawError?.response?.data?.details;

    if (!errorMessage) {
      return false;
    }

    const normalizedErrorMessage = String(errorMessage).toLowerCase();

    if (
      normalizedErrorMessage.includes("name") &&
      normalizedErrorMessage.includes("already exists")
    ) {
      showFieldValidationError("name", errorMessage);
      return true;
    }

    if (normalizedErrorMessage.includes("warehouse")) {
      showFieldValidationError("warehouse", errorMessage);
      return true;
    }

    if (normalizedErrorMessage.includes("category")) {
      showFieldValidationError("category_id", errorMessage);
      return true;
    }

    if (normalizedErrorMessage.includes("barcode_symbology")) {
      showFieldValidationError("barcode_symbology", errorMessage);
      return true;
    }

    if (normalizedErrorMessage.includes("item_code")) {
      showFieldValidationError("item_code", errorMessage);
      return true;
    }

    const isUnitFieldError =
      normalizedErrorMessage.includes("unit_id") ||
      normalizedErrorMessage.includes("unit of measure") ||
      normalizedErrorMessage.includes("unité de mesure") ||
      normalizedErrorMessage.includes("veuillez sélectionner une unité");

    if (isUnitFieldError) {
      showFieldValidationError("unit_id", errorMessage);
      return true;
    }

    if (
      normalizedErrorMessage.includes("material") ||
      normalizedErrorMessage.includes("intrant")
    ) {
      focusInvalidMaterialInput();
      message.error(errorMessage);
      return true;
    }

    if (normalizedErrorMessage.includes("output")) {
      showFieldValidationError("output_quantity", errorMessage);
      return true;
    }

    return false;
  };

  useEffect(() => {
    const warehouseId = watchedWarehouse || selectedWarehouse;
    if (!selectedCompany || !warehouseId) {
      setSuppliers([]);
      return undefined;
    }

    let cancelled = false;
    const loadSuppliers = async () => {
      setLoadingSuppliers(true);
      try {
        const response = await axios.get(`${API_URL}/api/users/suppliers`, {
          params: {
            companyId: selectedCompany,
            warehouseId,
          },
        });
        if (!cancelled) {
          setSuppliers(
            deduplicateEntities(
              Array.isArray(response.data) ? response.data : []
            )
          );
        }
      } catch (error) {
        if (!cancelled) {
          setSuppliers([]);
          console.error("[loadVariableCostSuppliers] Erreur :", error);
        }
      } finally {
        if (!cancelled) {
          setLoadingSuppliers(false);
        }
      }
    };

    loadSuppliers();
    return () => {
      cancelled = true;
    };
  }, [selectedCompany, selectedWarehouse, watchedWarehouse]);

  // La carte "Materiels de production" (amortissement) est reservee aux
  // entreprises au profil "Production / transformation".
  const isProductionActivity = selectedCompanyHasProfile(
    user,
    selectedCompany,
    "production"
  );

  // Charger le registre des materiels pour alimenter le selecteur d'amortissement.
  useEffect(() => {
    if (!isProductionActivity || !selectedWarehouse) {
      setAvailableEquipment([]);
      return undefined;
    }
    let cancelled = false;
    const loadEquipment = async () => {
      setLoadingEquipment(true);
      try {
        const response = await getProductionEquipment({
          warehouse_id: selectedWarehouse,
        });
        if (!cancelled) {
          setAvailableEquipment(
            Array.isArray(response?.equipment) ? response.equipment : []
          );
        }
      } catch (error) {
        if (!cancelled) {
          setAvailableEquipment([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingEquipment(false);
        }
      }
    };
    loadEquipment();
    return () => {
      cancelled = true;
    };
  }, [isProductionActivity, selectedWarehouse]);

  // Charger l'unité de production si en mode édition
  useEffect(() => {
    const init = async () => {
      if (isEditing) {
        await loadProductionUnit();
      }
      loadProducts();

      const bootstrapOk = await fetchBootstrap();
      if (!bootstrapOk) {
        fetchCategories();
        fetchExpenseCategories();
        fetchUnits();
        fetchWarehouses();
        fetchTaxes(); // AJOUT: Charger les taxes
      }
    };

    init();
  }, [isEditing, selectedCompany]);

  // Recharger les produits quand l'entrepôt change
  useEffect(() => {
    if (selectedWarehouse && !isEditing) {
      loadProducts();
    }
  }, [selectedWarehouse]);

  useEffect(() => {
    if (selectedWarehouse && !isEditing && !form.getFieldValue("warehouse")) {
      form.setFieldsValue({ warehouse: selectedWarehouse });
    }
  }, [form, isEditing, selectedWarehouse]);

  useEffect(() => {
    if (isEditing) {
      return;
    }

    const packagingLabel = String(watchedProductionPackagingLabel || "").trim();
    const containedQuantity = Number(watchedProductionPackagingContainedQuantity);
    const hasValidPackaging =
      Boolean(packagingLabel) &&
      Number.isFinite(containedQuantity) &&
      containedQuantity > 0;

    if (!hasValidPackaging || outputQuantityManuallyEditedRef.current) {
      return;
    }

    const currentOutputQuantity = Number(watchedOutputQuantity);
    const shouldAutoSync =
      !Number.isFinite(currentOutputQuantity) ||
      currentOutputQuantity <= 0 ||
      areCloseRecipeNumbers(currentOutputQuantity, 1) ||
      (lastAutoSyncedOutputQuantityRef.current !== null &&
        areCloseRecipeNumbers(
          currentOutputQuantity,
          lastAutoSyncedOutputQuantityRef.current
        ));

    if (!shouldAutoSync || areCloseRecipeNumbers(currentOutputQuantity, containedQuantity)) {
      return;
    }

    form.setFieldsValue({
      output_quantity: Number(containedQuantity.toFixed(4)),
    });
    lastAutoSyncedOutputQuantityRef.current = Number(
      containedQuantity.toFixed(4)
    );
  }, [
    form,
    isEditing,
    watchedOutputQuantity,
    watchedProductionPackagingContainedQuantity,
    watchedProductionPackagingLabel,
  ]);

  useEffect(() => {
    const outputQuantity = Number(watchedOutputQuantity);
    if (!Number.isFinite(outputQuantity) || outputQuantity <= 0) {
      return;
    }

    setMaterials((prevMaterials) => {
      let hasUpdate = false;
      const nextMaterials = prevMaterials.map((material) => {
        if (material.recipe_entry_mode === "usage_profile") {
          const selectedProfile = getSelectedMaterialUsageProfile(material);
          const calculatedQuantity = calculateUsageProfileMaterialQuantity(
            selectedProfile,
            outputQuantity
          );

          if (
            calculatedQuantity === null ||
            areCloseRecipeNumbers(material.quantity, calculatedQuantity)
          ) {
            return material;
          }

          hasUpdate = true;
          return {
            ...material,
            quantity: calculatedQuantity,
          };
        }

        // Re-stamp : une ligne "stock" posee avant que la quantite produite
        // soit renseignee recupere ici sa base de calibrage (quantite
        // inchangee), pour ne pas echapper definitivement a la detection.
        if (
          material.recipe_entry_mode === "stock" &&
          material.product_id &&
          !(Number(material.calibration_output_quantity) > 0)
        ) {
          hasUpdate = true;
          return {
            ...material,
            calibration_output_quantity: outputQuantity,
          };
        }

        return material;
      });

      return hasUpdate ? nextMaterials : prevMaterials;
    });
  }, [watchedOutputQuantity]);

  // Detection DERIVEE (lecture seule, aucun etat duplique) des intrants "stock"
  // desynchronises par rapport a la quantite produite courante.
  const staleMaterials = useMemo(() => {
    const cur = Number(watchedOutputQuantity);
    return materials.filter((material) => isStaleStock(material, cur));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materials, watchedOutputQuantity]);
  const hasStaleMaterials = staleMaterials.length > 0;

  // Transforms purs (utilises par les actions ET par le garde-fou au save).
  // `keys` optionnel : restreint l'action a certaines lignes (granularite par
  // ligne). Sans `keys`, traite toutes les lignes desynchronisees.
  const buildRecalcMaterials = (mats, cur, keys) =>
    mats.map((material) =>
      isStaleStock(material, cur) && (!keys || keys.includes(material.key))
        ? {
            ...material,
            quantity: recalcStockQuantity(material, cur),
            calibration_output_quantity: cur,
          }
        : material
    );

  // "Conserver" : quantite inchangee, mais la base est re-ancree a la quantite
  // produite courante et la ligne repasse en "manual" (coefficient moteur
  // abandonne) afin qu'un futur "Recalculer" parte de la valeur conservee.
  const buildKeepMaterials = (mats, cur, keys) =>
    mats.map((material) =>
      isStaleStock(material, cur) && (!keys || keys.includes(material.key))
        ? {
            ...material,
            calibration_output_quantity: cur,
            quantity_source: "manual",
            calibration_per_output: null,
          }
        : material
    );

  const recalcStaleMaterials = (keys) =>
    setMaterials((prev) =>
      buildRecalcMaterials(prev, getCurrentOutputQuantity(), keys)
    );

  const keepStaleMaterials = (keys) =>
    setMaterials((prev) =>
      buildKeepMaterials(prev, getCurrentOutputQuantity(), keys)
    );

  // Moteur d'assistance (local) : propose des recettes existantes similaires
  // d'apres le nom saisi. Debounce 500ms, uniquement en creation + mode assiste.
  // Le backend renvoie eligible:false pour les entreprises hors restauration.
  useEffect(() => {
    if (!recipeAssistantEnabled || isEditing) {
      setRecipeNameSuggestions([]);
      return undefined;
    }

    const name = String(watchedRecipeName || "").trim();
    if (name.length < 3 || !selectedCompany) {
      setRecipeNameSuggestions([]);
      return undefined;
    }

    if (recipeSuggestionTimerRef.current) {
      clearTimeout(recipeSuggestionTimerRef.current);
    }

    recipeSuggestionTimerRef.current = setTimeout(async () => {
      setLoadingRecipeSuggestions(true);
      try {
        const result = await getRecipeSuggestions({
          recipe_name: name,
          output_quantity: Number(watchedOutputQuantity) || undefined,
          company_id: selectedCompany,
          warehouse_id: selectedWarehouse || undefined,
          limit: 3,
        });
        setRecipeNameSuggestions(
          result && result.eligible && Array.isArray(result.suggestions)
            ? result.suggestions
            : []
        );
      } catch (error) {
        setRecipeNameSuggestions([]);
      } finally {
        setLoadingRecipeSuggestions(false);
      }
    }, 500);

    return () => {
      if (recipeSuggestionTimerRef.current) {
        clearTimeout(recipeSuggestionTimerRef.current);
      }
    };
  }, [
    recipeAssistantEnabled,
    isEditing,
    watchedRecipeName,
    watchedOutputQuantity,
    selectedCompany,
    selectedWarehouse,
  ]);

  // Signature des intrants deja saisis (par id de produit OU nom). Sert de
  // dependance stable : ne se rafraichit que si l'ENSEMBLE des intrants change
  // (pas a chaque modification de quantite).
  const presentMaterialsSignature = materials
    .map((item) => item.product_id || item.product_name || "")
    .filter(Boolean)
    .sort()
    .join("|");

  // Moteur d'assistance (local) : "ingredients souvent ajoutes". Se declenche
  // d'apres le nom (>=3 car.) ET l'ensemble des intrants. Debounce 800ms,
  // creation + mode assiste uniquement. Backend renvoie eligible:false hors
  // restauration -> liste vide, jamais bloquant.
  useEffect(() => {
    if (!recipeAssistantEnabled || isEditing) {
      setMissingIngredientSuggestions([]);
      return undefined;
    }

    const name = String(watchedRecipeName || "").trim();
    if (name.length < 3 || !selectedCompany) {
      setMissingIngredientSuggestions([]);
      return undefined;
    }

    if (missingIngredientTimerRef.current) {
      clearTimeout(missingIngredientTimerRef.current);
    }

    missingIngredientTimerRef.current = setTimeout(async () => {
      setLoadingMissingIngredients(true);
      try {
        const presentProductIds = materials
          .map((item) => item.product_id)
          .filter(Boolean);
        const presentNames = materials
          .map((item) => item.product_name)
          .filter(Boolean);
        const result = await getMissingIngredientSuggestions({
          recipe_name: name,
          output_quantity: Number(watchedOutputQuantity) || undefined,
          company_id: selectedCompany,
          warehouse_id: selectedWarehouse || undefined,
          present_product_ids: presentProductIds.join(",") || undefined,
          present_names: presentNames.join(",") || undefined,
          limit: 5,
        });
        setMissingIngredientSuggestions(
          result && result.eligible && Array.isArray(result.suggestions)
            ? result.suggestions
            : []
        );
      } catch (error) {
        setMissingIngredientSuggestions([]);
      } finally {
        setLoadingMissingIngredients(false);
      }
    }, 800);

    return () => {
      if (missingIngredientTimerRef.current) {
        clearTimeout(missingIngredientTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    recipeAssistantEnabled,
    isEditing,
    watchedRecipeName,
    watchedOutputQuantity,
    selectedCompany,
    selectedWarehouse,
    presentMaterialsSignature,
  ]);

  // Recharger les produits quand l'entreprise change
  useEffect(() => {
    if (selectedCompany) {
      // Recharger également les produits car l'entreprise peut affecter l'entrepôt sélectionné
      if (selectedWarehouse) {
        loadProducts();
      }
    }
  }, [selectedCompany]);

  // UseEffect pour récupérer les prix manquants après le chargement des matières premières
  useEffect(() => {
    if (isEditing && materials.length > 0 && selectedWarehouse) {
      const materialsWithoutPrices = materials.filter(
        (material) => !material.purchase_price || material.purchase_price === 0
      );

      if (materialsWithoutPrices.length > 0) {
        updateMissingPrices(materials);
      }
    }
  }, [materials.length, isEditing, selectedWarehouse]);

  // Charger l'unité de production
  const loadProductionUnit = async () => {
    setLoading(true);
    try {
      const data = await getProductionUnitById(id, {
        company_id: selectedCompany,
        warehouse_id: selectedWarehouse,
      });
      const normalizedLoadedBarcodeSymbology = normalizeBarcodeSymbology(
        data.barcode_symbology
      );
      const normalizedLoadedItemCode = normalizeItemCodeForSymbology(
        data.item_code,
        normalizedLoadedBarcodeSymbology
      );

      const loadedPackagingLabel =
        typeof data.production_packaging_label === "string"
          ? data.production_packaging_label.trim()
          : "";
      const loadedPackagingContainedQuantity =
        Number(data.production_packaging_contained_quantity) > 0
          ? Number(data.production_packaging_contained_quantity)
          : null;
      const loadedOutputCanonicalQuantity =
        data.outputs && data.outputs.length > 0
          ? Number(data.outputs[0].quantity) || 0
          : 0;
      const loadedHasPackaging =
        Boolean(loadedPackagingLabel) && loadedPackagingContainedQuantity;

      form.setFieldsValue({
        name: data.name,
        description: data.description,
        company_id: data.company_id,
        status: data.status === "active", // Convertir le statut textuel en booléen
        warehouse: data.warehouse_id,
        category_id: data.category_id,
        slug: data.slug,
        barcode_symbology: normalizedLoadedBarcodeSymbology,
        item_code: normalizedLoadedItemCode,
        stock_quantitiy_alert: data.stock_quantitiy_alert,
        unit_id: data.unit_id,
        output_quantity: loadedOutputCanonicalQuantity,
        production_kind:
          data.production_kind || PRODUCTION_KIND_FINISHED,
        sellable_without_production: !!data.sellable_without_production,
        production_packaging_label: loadedPackagingLabel || undefined,
        production_packaging_contained_quantity:
          loadedPackagingContainedQuantity || undefined,
        sales_price: data.sales_price,
        target_margin_rate:
          data.target_margin_rate !== null && data.target_margin_rate !== undefined
            ? Number(data.target_margin_rate)
            : undefined,
        tax_id: data.tax_id, // AJOUT: Charger la taxe
        created_at: data.created_at,
        updated_at: data.updated_at,
      });
      outputQuantityManuallyEditedRef.current = false;
      lastAutoSyncedOutputQuantityRef.current = loadedOutputCanonicalQuantity || null;
      setOutputQuantityInputMode(loadedHasPackaging ? "packaging" : "direct");

      // Si des produits de sortie existent, récupérer la quantité du premier
      // (généralement l'auto-référence à l'unité de production elle-même)
      if (data.outputs && data.outputs.length > 0) {
        // Utiliser la quantité du premier output comme quantité de sortie
        form.setFieldsValue({
          output_quantity: data.outputs[0].quantity,
        });
      }

      if (normalizedLoadedItemCode) {
        setBarcodeGenerated(true);
        setCurrentBarcode(normalizedLoadedItemCode);
      }

      // Configurer l'image si elle existe
      if (data.image) {
        // Créer un objet fileList compatible avec le composant Upload
        form.setFieldsValue({
          image: [
            {
              uid: "-1",
              name: data.image,
              status: "done",
              url: data.image,
            },
          ],
        });
      }

      // Configurer les matières premières et les produits finis
      const initialMaterials = data.materials.map((item) => ({
        key: item.id,
        product_id: item.product_id,
        product_name: item.product_name,
        quantity: item.quantity,
        purchase_price: item.purchase_price || 0, // Utiliser 0 par défaut si non disponible
        unit_short_name: item.unit_short_name,
        material_unit_id: item.material_unit_id || null,
        unit_name: item.unit_name || "",
        product_type: item.product_type || "",
        production_kind: item.production_kind || null,
        purchase_packaging_profiles: normalizePurchasePackagingProfiles(
          item.purchase_packaging_profiles
        ),
        usage_profiles: normalizeMaterialUsageProfiles(item.usage_profiles),
        recipe_entry_mode: "stock",
        selected_packaging_profile_id: null,
        selected_usage_profile_id: null,
        package_count: 1,
        // Base de calibrage = quantite produite reelle de la recette chargee
        // (outputs[0]). base === output au 1er rendu => aucun faux signal.
        calibration_output_quantity: loadedOutputCanonicalQuantity,
        quantity_source: "manual",
        calibration_per_output: null,
      }));

      setMaterials(initialMaterials);

      setVariableCosts(
        (data.variable_costs || []).map((item) => ({
          key: item.id || Date.now() + Math.random(),
          label: item.label || "",
          cost_per_unit: Number(item.cost_per_unit) || 0,
          supplier_id: item.supplier_id || null,
          supplier_name: item.supplier_name || "",
          expense_category_id: item.expense_category_id || null,
          expense_category_name: item.expense_category_name || "",
        }))
      );

      setEquipmentLinks(
        (data.unit_equipment || []).map((item) => ({
          key: item.id || Date.now() + Math.random(),
          equipment_id: item.equipment_id,
          equipment_name: item.equipment_name || "",
          oeuvre_coefficient:
            item.oeuvre_coefficient === undefined ||
            item.oeuvre_coefficient === null
              ? 1
              : Number(item.oeuvre_coefficient),
          depreciation_rate_per_unit:
            Number(item.depreciation_rate_per_unit) || 0,
        }))
      );

      setOutputs(
        data.outputs.map((item) => ({
          key: item.id,
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.quantity,
        }))
      );

      // Charger les produits utilisés dans cette unité pour les rendre disponibles dans les Select
      const usedProductIds = [];

      // Ajouter les IDs des matières premières
      if (data.materials && data.materials.length > 0) {
        data.materials.forEach((material) => {
          if (
            material.product_id &&
            !usedProductIds.includes(material.product_id)
          ) {
            usedProductIds.push(material.product_id);
          }
        });
      }

      // Ajouter les IDs des produits finis (sauf si c'est une auto-référence)
      if (data.outputs && data.outputs.length > 0) {
        data.outputs.forEach((output) => {
          if (
            output.product_id &&
            output.product_id !== parseInt(id) &&
            !usedProductIds.includes(output.product_id)
          ) {
            usedProductIds.push(output.product_id);
          }
        });
      }

      // Charger les détails de ces produits si nécessaire
      if (usedProductIds.length > 0) {
        try {
          const response = await axios.get(`${API_URL}/api/produits/by-ids`, {
            params: {
              ids: usedProductIds.join(","),
              warehouse_id: selectedWarehouse,
              company_id: selectedCompany,
            },
          });

          if (response.data && response.data.products) {
            // Ajouter ces produits à la liste existante (éviter les doublons)
            const newProducts = response.data.products.filter(
              (p) =>
                !products.some((existingProduct) =>
                  areSameEntityId(existingProduct.id, p.id)
                )
            );
            setProducts((prevProducts) => [...prevProducts, ...newProducts]);

            // Mettre à jour les matières premières avec les prix récupérés depuis l'API
            const updatedMaterials = initialMaterials.map((material) => {
              const productData = response.data.products.find((p) =>
                areSameEntityId(p.id, material.product_id)
              );
              if (
                productData &&
                ((productData.purchase_price && !material.purchase_price) ||
                  productData.unit_short_name ||
                  normalizePurchasePackagingProfiles(
                    productData.purchase_packaging_profiles
                  ).length > 0)
              ) {
                return {
                  ...material,
                  purchase_price:
                    productData.purchase_price || material.purchase_price,
                  unit_name: productData.unit_name || material.unit_name,
                  material_unit_id:
                    productData.unit_id || material.material_unit_id || null,
                  unit_short_name:
                    productData.unit_short_name || material.unit_short_name,
                  product_type: productData.product_type || material.product_type,
                  production_kind:
                    productData.production_kind || material.production_kind,
                  purchase_packaging_profiles: normalizePurchasePackagingProfiles(
                    productData.purchase_packaging_profiles
                  ),
                };
              }
              return material;
            });

            // Si des prix ont été mis à jour, mettre à jour le state
            const hasUpdatedMaterialData = updatedMaterials.some(
              (mat, index) =>
                mat.purchase_price !== initialMaterials[index].purchase_price ||
                mat.unit_short_name !== initialMaterials[index].unit_short_name ||
                getMaterialPackagingProfiles(mat).length !==
                  getMaterialPackagingProfiles(initialMaterials[index]).length
            );

            if (hasUpdatedMaterialData) {
              setMaterials(updatedMaterials);
            }
          }
        } catch (error) {
          console.error(
            "Erreur lors du chargement des produits utilisés:",
            error
          );
          // Continuer même si cette étape échoue
        }
      }

      // Récupérer les prix manquants pour les matières premières
      setTimeout(() => {
        updateMissingPrices(initialMaterials);
      }, 1000);
    } catch (error) {
      message.error(
        `Erreur lors du chargement de ${productionTerminology.unitSingular}`
      );
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Charger les produits pour les sélecteurs
  const loadInputProducts = async (searchTerm = "") => {
    const [rawData, semiFinishedData] = await Promise.all([
      getProducts(searchTerm, selectedWarehouse, "raw", {
        all: "true",
        limit: 1000,
        company_id: selectedCompany,
      }),
      getProducts(searchTerm, selectedWarehouse, "production", {
        all: "true",
        limit: 1000,
        production_kind: PRODUCTION_KIND_SEMI_FINISHED,
        company_id: selectedCompany,
      }),
    ]);

    return mergeProductsById(
      rawData?.products || [],
      semiFinishedData?.products || []
    );
  };

  // Charger les produits pour les sélecteurs
  const loadProducts = async () => {
    setLoadingProducts(true);
    try {
      const inputProducts = await loadInputProducts("");
      const productsWithoutPrices = inputProducts.filter(
        (product) => !product.purchase_price
      );

      if (productsWithoutPrices.length > 0) {
        console.warn(
          `${productsWithoutPrices.length} article(s) consommé(s) sans prix d'achat :`,
          productsWithoutPrices.map((product) => product.name)
        );
      }

      setProducts(inputProducts);
    } catch (error) {
      message.error("Erreur lors du chargement des produits");
      console.error("Erreur loadProducts:", error);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Rechercher des produits
  const handleProductSearch = async (
    value,
    productType = "input_materials"
  ) => {
    if (value.length > 2) {
      setLoadingProducts(true);
      try {
        if (productType === "input_materials") {
          const inputProducts = await loadInputProducts(value);
          setProducts(inputProducts);
        } else {
          const data = await getProducts(value, selectedWarehouse, productType, {
            all: "true",
            limit: 1000,
          });
          setProducts(data?.products || []);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingProducts(false);
      }
    }
  };

  // Récupération des catégories
  const fetchCategories = async () => {
    try {
      if (!selectedCompany) {
        setCategories([]);
        return;
      }

      const response = await axios.get(`${API_URL}/api/categories`, {
        params: { company_id: selectedCompany },
      });
      if (response.data) setCategories(response.data);
    } catch (error) {
      message.error("Erreur lors du chargement des catégories");
      console.error("[fetchCategories] Erreur :", error);
    }
  };

  const fetchExpenseCategories = async () => {
    try {
      if (!selectedCompany) {
        setExpenseCategories([]);
        return;
      }

      const response = await axios.get(`${API_URL}/api/expenses/categories`, {
        params: {
          company_id: selectedCompany,
          module_scope: "commercial",
        },
      });
      setExpenseCategories(
        Array.isArray(response.data) ? response.data : []
      );
    } catch (error) {
      setExpenseCategories([]);
      console.error("[fetchExpenseCategories] Erreur :", error);
    }
  };

  // Récupération des unités
  const fetchUnits = async () => {
    try {
      if (!selectedCompany) {
        // Si aucune entreprise n'est sélectionnée, vider la liste des unités
        setUnits([]);
        return;
      }

      const response = await axios.get(`${API_URL}/api/units`, {
        params: { company_id: selectedCompany, page: 1, limit: 1000 },
      });
      if (response.data && response.data.units) {
        setUnits(response.data.units);
      } else {
        setUnits([]);
      }
    } catch (error) {
      message.error("Erreur lors du chargement des unités");
      console.error("[fetchUnits] Erreur :", error);
      setUnits([]);
    }
  };

  // Récupération des entrepôts
  const fetchWarehouses = async () => {
    try {
      if (!selectedCompany) {
        setWarehouses([]);
        return;
      }

      const response = await axios.get(`${API_URL}/api/warehouses`, {
        params: { company_id: selectedCompany },
      });
      if (response.data && response.data.warehouses) {
        setWarehouses(response.data.warehouses);
      } else {
        setWarehouses([]);
      }
    } catch (error) {
      message.error("Erreur lors du chargement des établissements");
      console.error("[fetchWarehouses] Erreur :", error);
    }
  };

  // AJOUT: Récupération des taxes
  const fetchTaxes = async () => {
    try {
      if (!selectedCompany) {
        setTaxes([]);
        return;
      }

      const response = await axios.get(`${API_URL}/api/taxes`, {
        params: { companyId: selectedCompany },
      });
      if (response.data && response.data.taxes) {
        setTaxes(response.data.taxes);
      } else {
        setTaxes([]);
      }
    } catch (error) {
      message.error("Erreur lors du chargement des taxes");
      console.error("[fetchTaxes] Erreur :", error);
      setTaxes([]);
    }
  };

  // Bootstrap agrégé (1 call au lieu de 4) pour limiter le burst initial MySQL
  const fetchBootstrap = async () => {
    try {
      if (!selectedCompany) {
        setCategories([]);
        setExpenseCategories([]);
        setUnits([]);
        setWarehouses([]);
        setTaxes([]);
        return true;
      }

      const response = await axios.get(`${API_URL}/api/production/bootstrap`, {
        params: { company_id: selectedCompany },
      });

      const data = response.data || {};
      setCategories(Array.isArray(data.categories) ? data.categories : []);
      setExpenseCategories(
        Array.isArray(data.expense_categories) ? data.expense_categories : []
      );
      setUnits(Array.isArray(data.units) ? data.units : []);
      setWarehouses(Array.isArray(data.warehouses) ? data.warehouses : []);
      setTaxes(Array.isArray(data.taxes) ? data.taxes : []);
      return true;
    } catch (error) {
      console.error("[fetchBootstrap] Erreur :", error);
      return false;
    }
  };

  // Fonction pour afficher les métadonnées de création et modification
  const renderTimestamps = () => {
    if (!isEditing) return null;

    // Récupérer les dates depuis l'API
    const createdAt = form.getFieldValue("created_at");
    const updatedAt = form.getFieldValue("updated_at");

    if (!createdAt && !updatedAt) return null;

    return (
      <Card style={{ ...formStyles.card, marginTop: 16 }}>
        <Row gutter={16}>
          {createdAt && (
            <Col span={12}>
              <Text type="secondary">Créé le:</Text>
              <div>
                <Text strong>{new Date(createdAt).toLocaleString()}</Text>
              </div>
            </Col>
          )}
          {updatedAt && (
            <Col span={12}>
              <Text type="secondary">Dernière modification:</Text>
              <div>
                <Text strong>{new Date(updatedAt).toLocaleString()}</Text>
              </div>
            </Col>
          )}
        </Row>
      </Card>
    );
  };

  // Génération du slug
  const generateSlug = (name) =>
    name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  // Génération du code article
  const generateArticleCode = (name, symbology = "CODE128") => {
    const normalizedSymbology = normalizeBarcodeSymbology(symbology);

    if (normalizedSymbology === "EAN13") {
      return generateEan13Base();
    }

    const cleanName = name
      .toUpperCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^A-Z0-9]/g, "");
    const prefix = cleanName.slice(0, 3);
    const timestamp = Date.now().toString().slice(-5);

    if (normalizedSymbology === "CODE39") {
      return `${prefix}${timestamp}`.replace(/[^0-9A-Z\-. $/+%]/g, "");
    }

    return `${prefix}${timestamp}`;
  };

  // Génération du code-barres
  const generateBarcode = async () => {
    try {
      const type = normalizeBarcodeSymbology(form.getFieldValue("barcode_symbology"));
      if (!type) {
        message.error("Veuillez d'abord sélectionner un type de code-barres");
        return;
      }
      const response = await axios.get(
        `${API_URL}/api/produits/generate-barcode`,
        {
          params: { type },
        }
      );
      if (response.data.success) {
        const newBarcode = response.data.barcode;
        form.setFieldsValue({ barcode_symbology: type, item_code: newBarcode });
        form.setFields([{ name: ["item_code"], errors: [] }]);
        setCurrentBarcode(newBarcode);
        setBarcodeGenerated(true);
        message.success("Code-barres généré avec succès");
      }
    } catch (error) {
      message.error("Erreur lors de la génération du code-barres");
      console.error("[generateBarcode] Erreur :", error);
    }
  };

  // Affichage du code-barres
  const showBarcode = () => {
    const currentBarcodeValue = form.getFieldValue("item_code");
    const barcodeType = normalizeBarcodeSymbology(
      form.getFieldValue("barcode_symbology")
    );

    const normalizedItemCode = normalizeItemCodeForSymbology(
      currentBarcodeValue,
      barcodeType
    );

    if (!normalizedItemCode) {
      showFieldValidationError(
        "item_code",
        "Veuillez entrer ou générer un code article."
      );
      return;
    }

    if (!isValidItemCodeForSymbology(normalizedItemCode, barcodeType)) {
      const validationMessage =
        barcodeType === "EAN13"
          ? "Pour le format EAN13, le code doit contenir 12 ou 13 chiffres."
          : "Le code article contient des caractères incompatibles avec la symbologie sélectionnée.";
      showFieldValidationError("item_code", validationMessage);
      return;
    }

    form.setFieldsValue({ item_code: normalizedItemCode });
    setCurrentBarcode(normalizedItemCode);
    setBarcodeModalVisible(true);
  };

  // Ajouter une matière première
  const addMaterial = () => {
    const newMaterial = {
      key: Date.now(),
      product_id: undefined,
      product_type: "",
      quantity: 1,
      purchase_price: 0,
      unit_name: "",
      unit_short_name: "",
      material_unit_id: null,
      purchase_packaging_profiles: [],
      usage_profiles: [],
      suggested_usage_profiles: [],
      loading_usage_profile_suggestions: false,
      recipe_entry_mode: "stock",
      selected_packaging_profile_id: null,
      selected_usage_profile_id: null,
      package_count: 1,
      calibration_output_quantity: getCurrentOutputQuantity() || null,
      quantity_source: "manual",
      calibration_per_output: null,
    };
    setMaterials([...materials, newMaterial]);
  };

  // Supprimer une matière première
  const removeMaterial = (key) => {
    setMaterials(materials.filter((item) => item.key !== key));
  };

  // Moteur d'assistance (local) : applique une recette suggeree en remplissant
  // la liste des intrants. Les quantites sont deja normalisees a la quantite
  // produite courante par le backend ; l'utilisateur peut les ajuster ensuite.
  const applyRecipeSuggestion = (suggestion) => {
    if (!suggestion || !Array.isArray(suggestion.ingredients)) {
      return;
    }

    const baseTimestamp = Date.now();
    const recipeBaseOutput = Number(suggestion.base_output_quantity);
    const currentOutput = getCurrentOutputQuantity();
    const newMaterials = suggestion.ingredients.map((ingredient, index) => {
      const product = findProductById(ingredient.product_id);
      // Coefficient par-sortie au niveau RECETTE (base_quantity / base_output).
      // Recalcul exact et immunise contre une divergence entre la quantite
      // produite au moment du fetch et celle au moment de l'application.
      const perOutput =
        recipeBaseOutput > 0 && Number(ingredient.base_quantity) > 0
          ? Number(ingredient.base_quantity) / recipeBaseOutput
          : null;
      const calibratedQuantity =
        perOutput !== null && currentOutput > 0
          ? round2(perOutput * currentOutput)
          : Number(ingredient.quantity) > 0
          ? Number(ingredient.quantity)
          : 1;
      return {
        key: baseTimestamp + index,
        product_id: ingredient.product_id || undefined,
        product_name: product?.name || ingredient.name || "",
        quantity: calibratedQuantity,
        purchase_price: product?.purchase_price || 0,
        unit_name: product?.unit_name || "",
        unit_short_name: product?.unit_short_name || ingredient.unit || "",
        material_unit_id: product?.unit_id || ingredient.unit_id || null,
        product_type: product?.product_type || "",
        production_kind: product?.production_kind || null,
        purchase_packaging_profiles: normalizePurchasePackagingProfiles(
          product?.purchase_packaging_profiles
        ),
        usage_profiles: [],
        suggested_usage_profiles: [],
        loading_usage_profile_suggestions: false,
        recipe_entry_mode: "stock",
        selected_packaging_profile_id: null,
        selected_usage_profile_id: null,
        package_count: 1,
        calibration_output_quantity: currentOutput || recipeBaseOutput || null,
        quantity_source: perOutput !== null ? "engine" : "manual",
        calibration_per_output: perOutput,
      };
    });

    setMaterials(newMaterials);
    setRecipeNameSuggestions([]);
    message.success(
      `${productionTerminology.configurationSingularTitle} « ${suggestion.recipe_name} » appliquée. Ajustez les quantités si besoin.`
    );

    // Completer les prix manquants depuis l'API (mode creation).
    if (selectedWarehouse) {
      newMaterials.forEach((material) => {
        if (material.product_id && !material.purchase_price) {
          fetchProductPriceDetails(material.product_id, material.key);
        }
      });
    }
  };

  // Moteur d'assistance (local) : ajoute UN ingredient manquant suggere comme
  // nouvelle ligne pre-remplie. Reutilise le meme gabarit de ligne que
  // applyRecipeSuggestion (calibrage par-sortie quand disponible).
  const applyMissingIngredient = (suggestion) => {
    if (!suggestion) {
      return;
    }
    const product = suggestion.product_id
      ? findProductById(suggestion.product_id)
      : null;
    const currentOutput = getCurrentOutputQuantity();
    const perOutput =
      Number(suggestion.per_output_quantity) > 0
        ? Number(suggestion.per_output_quantity)
        : null;
    const calibratedQuantity =
      perOutput !== null && currentOutput > 0
        ? round2(perOutput * currentOutput)
        : Number(suggestion.quantity) > 0
        ? Number(suggestion.quantity)
        : 1;

    const newMaterial = {
      key: Date.now(),
      product_id: suggestion.product_id || undefined,
      product_name: product?.name || suggestion.name || "",
      quantity: calibratedQuantity,
      purchase_price: product?.purchase_price || 0,
      unit_name: product?.unit_name || "",
      unit_short_name: product?.unit_short_name || suggestion.unit || "",
      material_unit_id: product?.unit_id || suggestion.unit_id || null,
      product_type: product?.product_type || "",
      production_kind: product?.production_kind || null,
      purchase_packaging_profiles: normalizePurchasePackagingProfiles(
        product?.purchase_packaging_profiles
      ),
      usage_profiles: [],
      suggested_usage_profiles: [],
      loading_usage_profile_suggestions: false,
      recipe_entry_mode: "stock",
      selected_packaging_profile_id: null,
      selected_usage_profile_id: null,
      package_count: 1,
      calibration_output_quantity: currentOutput || null,
      quantity_source: perOutput !== null ? "engine" : "manual",
      calibration_per_output: perOutput,
    };

    setMaterials((prev) => [...prev, newMaterial]);
    // L'ingredient devient present : le retirer des suggestions.
    setMissingIngredientSuggestions((prev) =>
      prev.filter((item) => item !== suggestion)
    );

    // Completer le prix manquant depuis l'API (mode creation).
    if (
      selectedWarehouse &&
      newMaterial.product_id &&
      !newMaterial.purchase_price
    ) {
      fetchProductPriceDetails(newMaterial.product_id, newMaterial.key);
    }

    message.success(
      `${productionTerminology.materialSingularTitle} « ${newMaterial.product_name} » ${productionTerminology.materialAddedAgreement}. Ajustez la quantité si besoin.`
    );
  };

  // Fonction pour récupérer les détails de prix d'un produit spécifique
  const fetchMaterialUsageProfileSuggestions = async (productId, materialKey) => {
    if (!productId || !selectedCompany) {
      return;
    }

    setMaterials((prevMaterials) =>
      prevMaterials.map((item) =>
        item.key === materialKey
          ? { ...item, loading_usage_profile_suggestions: true }
          : item
      )
    );

    try {
      const response = await axios.get(
        `${API_URL}/api/production/material-usage-profiles`,
        {
          params: {
            material_product_id: productId,
            company_id: selectedCompany,
            warehouse_id: selectedWarehouse || undefined,
            exclude_production_unit_id: isEditing ? id : undefined,
            limit: 25,
          },
        }
      );
      const suggestions = normalizeMaterialUsageProfiles(
        response.data?.profiles || []
      );

      setMaterials((prevMaterials) =>
        prevMaterials.map((item) =>
          item.key === materialKey && areSameEntityId(item.product_id, productId)
            ? {
                ...item,
                suggested_usage_profiles: suggestions,
                loading_usage_profile_suggestions: false,
              }
            : item
        )
      );
    } catch (error) {
      setMaterials((prevMaterials) =>
        prevMaterials.map((item) =>
          item.key === materialKey
            ? { ...item, loading_usage_profile_suggestions: false }
            : item
        )
      );
      console.error(
        "Erreur lors du chargement des profils recette suggérés:",
        error
      );
    }
  };

  // Moteur d'assistance (local) : quantite calibree suggeree pour un ingredient.
  // Stockee sur la ligne (semantic_quantity_suggestions) et proposee dans le
  // tableau. Confort uniquement : jamais bloquante, silencieuse en cas d'echec.
  const fetchIngredientQuantitySuggestion = async (productId, materialKey) => {
    if (!recipeAssistantEnabled || !productId || !selectedCompany) {
      return;
    }
    const product = findProductById(productId);
    const ingredientName = product?.name;
    if (!ingredientName) {
      return;
    }
    try {
      const result = await getIngredientSuggestions({
        ingredient_name: ingredientName,
        recipe_name: form.getFieldValue("name") || undefined,
        output_quantity:
          Number(form.getFieldValue("output_quantity")) || undefined,
        company_id: selectedCompany,
        warehouse_id: selectedWarehouse || undefined,
        exclude_production_unit_id: isEditing ? id : undefined,
      });
      const suggestions =
        result && result.eligible && Array.isArray(result.suggestions)
          ? result.suggestions
          : [];
      setMaterials((prevMaterials) =>
        prevMaterials.map((item) =>
          item.key === materialKey &&
          areSameEntityId(item.product_id, productId)
            ? { ...item, semantic_quantity_suggestions: suggestions }
            : item
        )
      );
    } catch (error) {
      // silencieux : la suggestion est un confort, jamais bloquante
    }
  };

  const fetchProductPriceDetails = async (productId, materialKey) => {
    try {
      const response = await axios.get(`${API_URL}/api/produits/${productId}`, {
        params: {
          warehouse_id: selectedWarehouse,
          company_id: selectedCompany,
          with_stock: true,
        },
      });

      if (response.data) {
        const productData = response.data;
        const normalizedProfiles = normalizePurchasePackagingProfiles(
          productData.purchase_packaging_profiles
        );
        // Mettre à jour la matière première avec les détails récupérés
        setMaterials((prevMaterials) =>
          prevMaterials.map((item) => {
            if (
              item.key !== materialKey ||
              !areSameEntityId(item.product_id, productId)
            ) {
              return item;
            }

            const autoEnablePackaging = shouldAutoEnablePackagingForMaterial(
              item,
              productData
            );

            return syncMaterialPackagingSelection(
              {
                ...item,
                product_name: productData.name || item.product_name,
                purchase_price: productData.purchase_price || 0,
                unit_name: productData.unit_name || item.unit_name,
                material_unit_id: productData.unit_id || item.material_unit_id,
                unit_short_name:
                  productData.unit_short_name || item.unit_short_name,
                product_type: productData.product_type || item.product_type,
                production_kind:
                  productData.production_kind || item.production_kind,
                purchase_packaging_profiles: normalizedProfiles,
                recipe_entry_mode: autoEnablePackaging
                  ? "packaging"
                  : item.recipe_entry_mode || "stock",
                selected_packaging_profile_id: autoEnablePackaging
                  ? getDefaultPurchasePackagingProfile(normalizedProfiles)?.id ??
                    item.selected_packaging_profile_id
                  : item.selected_packaging_profile_id,
              },
              {},
              {
                preserveQuantity: !autoEnablePackaging,
              }
            );
          })
        );

        // Également mettre à jour la liste des produits pour éviter de refaire l'appel
        setProducts((prevProducts) => {
          const existingIndex = prevProducts.findIndex((p) =>
            areSameEntityId(p.id, productId)
          );
          if (existingIndex >= 0) {
            const updatedProducts = [...prevProducts];
            updatedProducts[existingIndex] = {
              ...updatedProducts[existingIndex],
              product_type: productData.product_type,
              production_kind: productData.production_kind,
              purchase_price: productData.purchase_price,
              unit_name: productData.unit_name,
              unit_short_name: productData.unit_short_name,
              purchase_packaging_profiles: normalizedProfiles,
            };
            return updatedProducts;
          }
          return [
            ...prevProducts,
            {
              ...productData,
              purchase_packaging_profiles: normalizedProfiles,
            },
          ];
        });
      }
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des détails du produit:",
        error
      );
      // Ne pas afficher d'erreur à l'utilisateur pour ne pas le perturber
    }
  };


  const handleMaterialChange = (key, field, value) => {
    if (field === "product_id") {
      if (
        value &&
        materials.some(
          (material) =>
            material.key !== key && areSameEntityId(material.product_id, value)
        )
      ) {
        message.warning(
          `${productionTerminology.materialDemonstrativeSingular} est déjà ${productionTerminology.materialAddedAgreement}. Modifiez plutôt sa quantité si besoin.`
        );
        return;
      }

      const product = findProductById(value);
      const normalizedProfiles = normalizePurchasePackagingProfiles(
        product?.purchase_packaging_profiles
      );
      const canStartInPackaging = false;
      const shouldFetchDetails =
        Boolean(selectedWarehouse) &&
        (!product ||
          !product.purchase_price ||
          !product.unit_short_name ||
          (String(product?.product_type || "").toLowerCase() === "raw" &&
            normalizedProfiles.length === 0));

      setMaterials((prevMaterials) =>
        prevMaterials.map((item) => {
          if (item.key !== key) {
            return item;
          }

          return syncMaterialPackagingSelection(
            {
              ...item,
              [field]: value,
              product_name: product?.name || "",
              purchase_price: Number(product?.purchase_price) || 0,
              unit_name: product?.unit_name || "",
              unit_short_name: product?.unit_short_name || "",
              material_unit_id: product?.unit_id || null,
              product_type: product?.product_type || "",
              production_kind: product?.production_kind || null,
              purchase_packaging_profiles: normalizedProfiles,
              usage_profiles: [],
              recipe_entry_mode: canStartInPackaging ? "packaging" : "stock",
              selected_packaging_profile_id: canStartInPackaging
                ? getDefaultPurchasePackagingProfile(normalizedProfiles)?.id ??
                  null
                : null,
              selected_usage_profile_id: null,
              package_count: 1,
              quantity: 1,
              calibration_output_quantity: getCurrentOutputQuantity() || null,
              quantity_source: "manual",
              calibration_per_output: null,
            },
            {},
            {
              preserveQuantity: !canStartInPackaging,
            }
          );
        })
      );

      if (shouldFetchDetails) {
        fetchProductPriceDetails(value, key);
      }
      fetchMaterialUsageProfileSuggestions(value, key);
      fetchIngredientQuantitySuggestion(value, key);

      return;
    }

    setMaterials((prevMaterials) =>
      prevMaterials.map((item) => {
        if (item.key !== key) {
          return item;
        }
        if (field === "quantity") {
          // Une saisie clavier confirme la base a la quantite produite courante
          // et abandonne tout coefficient moteur (valeur absolue voulue).
          return {
            ...item,
            quantity: value,
            calibration_output_quantity:
              getCurrentOutputQuantity() || item.calibration_output_quantity,
            quantity_source: "manual",
            calibration_per_output: null,
          };
        }
        return { ...item, [field]: value };
      })
    );
  };

  const handleMaterialEntryModeChange = (key, nextMode) => {
    setMaterials((prevMaterials) =>
      prevMaterials.map((item) => {
        if (item.key !== key) {
          return item;
        }

        if (nextMode === "usage_profile") {
          if (
            item.product_id &&
            !item.loading_usage_profile_suggestions &&
            getSuggestedMaterialUsageProfiles(item).length === 0
          ) {
            fetchMaterialUsageProfileSuggestions(item.product_id, item.key);
          }

          return syncMaterialUsageProfileSelection({
            ...item,
            recipe_entry_mode: "usage_profile",
          });
        }

        if (nextMode === "stock") {
          // La quantite affichee devient une valeur absolue calibree a la
          // quantite produite courante a cet instant.
          return syncMaterialPackagingSelection(
            {
              ...item,
              recipe_entry_mode: "stock",
              calibration_output_quantity: getCurrentOutputQuantity() || null,
              quantity_source: "manual",
              calibration_per_output: null,
            },
            {},
            {
              preserveQuantity: true,
            }
          );
        }

        return syncMaterialPackagingSelection(
          {
            ...item,
            recipe_entry_mode: nextMode,
          },
          {},
          {
            preserveQuantity: nextMode !== "packaging",
          }
        );
      })
    );
  };

  const handleMaterialPackagingProfileChange = (key, profileId) => {
    setMaterials((prevMaterials) =>
      prevMaterials.map((item) =>
        item.key === key
          ? syncMaterialPackagingSelection({
              ...item,
              recipe_entry_mode: "packaging",
              selected_packaging_profile_id: profileId,
            })
          : item
      )
    );
  };

  const handleMaterialPackagingCountChange = (key, value) => {
    setMaterials((prevMaterials) =>
      prevMaterials.map((item) =>
        item.key === key
          ? syncMaterialPackagingSelection({
              ...item,
              recipe_entry_mode: "packaging",
              package_count: normalizePositiveNumber(value, 1),
            })
          : item
      )
    );
  };

  const getOutputUnitShortName = () => {
    const outputUnitId = form.getFieldValue("unit_id");
    return outputUnitId
      ? units.find((unit) => unit.id === outputUnitId)?.short_name || "unité"
      : "unité";
  };

  const openUsageProfileModal = (materialKey) => {
    const material = materials.find((item) => item.key === materialKey);

    if (!material?.product_id) {
      message.warning(
        `Sélectionnez d'abord ${productionTerminology.materialIndefiniteSingular}.`
      );
      return;
    }

    const outputQuantity = normalizePositiveNumber(
      form.getFieldValue("output_quantity"),
      1
    );
    const profiles = getMaterialUsageProfiles(material);

    setUsageProfileModal({
      materialKey,
      label: `Profil ${formatRecipeQuantity(outputQuantity)} ${getOutputUnitShortName()}`,
      reference_output_quantity: outputQuantity,
      material_quantity: normalizePositiveNumber(material.quantity, 1),
      loss_rate: 0,
      yield_rate: 0,
      notes: "",
      is_default: profiles.length === 0,
    });
  };

  const updateUsageProfileModal = (field, value) => {
    setUsageProfileModal((current) =>
      current
        ? {
            ...current,
            [field]: value,
          }
        : current
    );
  };

  const closeUsageProfileModal = () => {
    setUsageProfileModal(null);
  };

  const saveUsageProfileModal = () => {
    if (!usageProfileModal) {
      return;
    }

    const material = materials.find(
      (item) => item.key === usageProfileModal.materialKey
    );
    const label = String(usageProfileModal.label || "").trim();
    const referenceOutputQuantity = normalizePositiveNumber(
      usageProfileModal.reference_output_quantity
    );
    const materialQuantity = normalizePositiveNumber(
      usageProfileModal.material_quantity
    );

    if (!material?.product_id || !label || !referenceOutputQuantity || !materialQuantity) {
      message.warning(
        "Renseignez le libellé, la sortie de référence et la quantité consommée."
      );
      return;
    }

    const nextProfile = {
      client_key: `usage-${Date.now()}-${Math.round(Math.random() * 100000)}`,
      material_product_id: parseInt(material.product_id, 10),
      label,
      reference_output_quantity: referenceOutputQuantity,
      reference_output_unit_id: form.getFieldValue("unit_id") || null,
      material_quantity: materialQuantity,
      material_unit_id: material.material_unit_id || null,
      loss_rate: normalizePositiveNumber(usageProfileModal.loss_rate, 0),
      yield_rate: normalizePositiveNumber(usageProfileModal.yield_rate, 0),
      notes: String(usageProfileModal.notes || "").trim(),
      is_default: Boolean(usageProfileModal.is_default),
    };

    setMaterials((prevMaterials) =>
      prevMaterials.map((item) => {
        if (item.key !== usageProfileModal.materialKey) {
          return item;
        }

        const existingProfiles = getMaterialUsageProfiles(item);
        const shouldSetDefault =
          nextProfile.is_default || existingProfiles.length === 0;
        const nextProfiles = normalizeMaterialUsageProfiles([
          ...existingProfiles.map((profile) => ({
            ...profile,
            is_default: shouldSetDefault ? false : profile.is_default,
          })),
          {
            ...nextProfile,
            is_default: shouldSetDefault,
          },
        ]);

        return syncMaterialUsageProfileSelection({
          ...item,
          usage_profiles: nextProfiles,
          recipe_entry_mode: "usage_profile",
          selected_usage_profile_id: nextProfile.client_key,
        });
      })
    );

    closeUsageProfileModal();
    message.success(`${productionTerminology.usageProfileSingularTitle} ajouté.`);
  };

  const handleMaterialUsageProfileChange = (key, profileId) => {
    setMaterials((prevMaterials) =>
      prevMaterials.map((item) =>
        item.key === key
          ? syncMaterialUsageProfileSelection({
              ...item,
              recipe_entry_mode: "usage_profile",
              selected_usage_profile_id: profileId,
            })
          : item
      )
    );
  };

  const importSuggestedMaterialUsageProfile = (key, profileId) => {
    setMaterials((prevMaterials) =>
      prevMaterials.map((item) => {
        if (item.key !== key) {
          return item;
        }

        const suggestion = getSuggestedMaterialUsageProfiles(item).find(
          (profile) =>
            areSameEntityId(profile.id, profileId) ||
            profile.client_key === profileId
        );

        if (!suggestion) {
          return item;
        }

        const nextProfile = {
          ...suggestion,
          id: null,
          client_key: `usage-${Date.now()}-${Math.round(
            Math.random() * 100000
          )}`,
          material_product_id: item.product_id,
          reference_output_unit_id: form.getFieldValue("unit_id") || null,
          material_unit_id: item.material_unit_id || suggestion.material_unit_id,
          is_default: getMaterialUsageProfiles(item).length === 0,
        };
        const nextProfiles = normalizeMaterialUsageProfiles([
          ...getMaterialUsageProfiles(item),
          nextProfile,
        ]);

        return syncMaterialUsageProfileSelection({
          ...item,
          usage_profiles: nextProfiles,
          recipe_entry_mode: "usage_profile",
          selected_usage_profile_id: nextProfile.client_key,
        });
      })
    );

    message.success(
      `Profil importé dans cette ${productionTerminology.configurationSingular}.`
    );
  };

  const removeSelectedMaterialUsageProfile = (key) => {
    setMaterials((prevMaterials) =>
      prevMaterials.map((item) => {
        if (item.key !== key) {
          return item;
        }

        const selectedProfile = getSelectedMaterialUsageProfile(item);
        if (!selectedProfile) {
          return item;
        }

        const nextProfiles = getMaterialUsageProfiles(item).filter(
          (profile) =>
            !(
              areSameEntityId(profile.id, selectedProfile.id) ||
              profile.client_key === selectedProfile.client_key
            )
        );

        return {
          ...item,
          usage_profiles: nextProfiles,
          recipe_entry_mode: "stock",
          selected_usage_profile_id: null,
        };
      })
    );
  };

  const addVariableCost = () => {
    const newCost = {
      key: Date.now(),
      label: "",
      cost_per_unit: 0,
      supplier_id: null,
      expense_category_id: null,
    };
    setVariableCosts([...variableCosts, newCost]);
  };

  const removeVariableCost = (key) => {
    setVariableCosts(variableCosts.filter((item) => item.key !== key));
  };

  const updateVariableCost = (key, field, value) => {
    setVariableCosts(
      variableCosts.map((item) =>
        item.key === key ? { ...item, [field]: value } : item
      )
    );
  };

  const addEquipmentLink = () => {
    setEquipmentLinks([
      ...equipmentLinks,
      {
        key: Date.now(),
        equipment_id: null,
        equipment_name: "",
        oeuvre_coefficient: 1,
        depreciation_rate_per_unit: 0,
      },
    ]);
  };

  const removeEquipmentLink = (key) => {
    setEquipmentLinks(equipmentLinks.filter((item) => item.key !== key));
  };

  const updateEquipmentLink = (key, field, value) => {
    setEquipmentLinks(
      equipmentLinks.map((item) => {
        if (item.key !== key) {
          return item;
        }
        if (field === "equipment_id") {
          const selected = availableEquipment.find(
            (equipment) => Number(equipment.id) === Number(value)
          );
          return {
            ...item,
            equipment_id: value,
            equipment_name: selected?.name || "",
            depreciation_rate_per_unit:
              Number(selected?.depreciation_rate_per_unit) || 0,
          };
        }
        return { ...item, [field]: value };
      })
    );
  };

  // Fermer le modal
  const handleModalClose = () => {
    setModalVisible(false);
    navigate("/production/units");
  };

  // Suggérer un prix de vente depuis le cout de revient et la marge cible.
  const handleSuggestSalesPrice = async () => {
    const values = form.getFieldsValue(true);
    const targetMarginRate = Number(values.target_margin_rate);
    const outputQuantity = Number(values.output_quantity) || 1;
    const warehouseId = Number(values.warehouse || selectedWarehouse);
    const selectedWarehouseData = warehouses.find(
      (warehouse) => Number(warehouse.id) === warehouseId
    );
    const companyId = Number(selectedWarehouseData?.company_id || selectedCompany);

    if (!warehouseId) {
      showFieldValidationError("warehouse", "Veuillez sélectionner un établissement.");
      return;
    }

    if (!companyId) {
      message.error("Contexte entreprise introuvable pour cette suggestion.");
      return;
    }

    if (!Number.isFinite(outputQuantity) || outputQuantity <= 0) {
      showFieldValidationError(
        "output_quantity",
        "Veuillez saisir une quantité produite valide."
      );
      return;
    }

    if (
      !Number.isFinite(targetMarginRate) ||
      targetMarginRate < 0 ||
      targetMarginRate >= 100
    ) {
      showFieldValidationError(
        "target_margin_rate",
        "Le taux de marge cible doit être compris entre 0 et 99,99."
      );
      return;
    }

    const materialsJson = materials.map(({ product_id, quantity }) => ({
      product_id: parseInt(product_id, 10),
      quantity: parseFloat(quantity),
    }));

    if (
      materialsJson.length === 0 ||
      materialsJson.some(
        (item) =>
          Number.isNaN(item.product_id) ||
          !Number.isFinite(item.quantity) ||
          item.quantity <= 0
      )
    ) {
      message.error(
        `Veuillez renseigner correctement chaque ${productionTerminology.materialSingular} avant de suggérer un prix.`
      );
      focusInvalidMaterialInput();
      return;
    }

    const variableCostsJson = variableCosts
      .map((item) => ({
        label: String(item.label || "").trim(),
        cost_per_unit: parseFloat(item.cost_per_unit) || 0,
      }))
      .filter((item) => item.label.length > 0);

    const unitEquipmentJson = equipmentLinks
      .map((item) => ({
        equipment_id: Number(item.equipment_id) || null,
        oeuvre_coefficient:
          item.oeuvre_coefficient === undefined ||
          item.oeuvre_coefficient === null
            ? 1
            : Number(item.oeuvre_coefficient),
      }))
      .filter((item) => item.equipment_id);

    setSuggestingPrice(true);
    try {
      const response = await suggestProductionUnitPrice({
        company_id: companyId,
        warehouse_id: warehouseId,
        output_quantity: outputQuantity,
        target_margin_rate: targetMarginRate,
        materials: materialsJson,
        variable_costs: variableCostsJson,
        unit_equipment: unitEquipmentJson,
      });
      const suggestion = response?.suggestion;

      if (
        !suggestion ||
        !Number.isFinite(Number(suggestion.suggested_sales_price))
      ) {
        throw new Error("Suggestion de prix indisponible.");
      }

      setPriceSuggestion(suggestion);
      form.setFieldsValue({
        sales_price: Number(suggestion.suggested_sales_price),
      });
      message.success("Prix de vente suggéré appliqué.");
    } catch (error) {
      const errorMessage =
        error?.error ||
        error?.message ||
        "Erreur lors de la suggestion du prix de vente.";
      message.error(errorMessage);
    } finally {
      setSuggestingPrice(false);
    }
  };

  // Gérer la soumission du formulaire
  const handleSubmit = async (values) => {
    setSubmitting(true);

    const requiredPermission = isEditing
      ? "Gestion Commerciale.Approvisionnement.Production.Unites.edit"
      : "Gestion Commerciale.Approvisionnement.Production.Unites.create";

    if (!hasPermission(requiredPermission)) {
      message.error(
        `Vous n'avez pas la permission de ${
          isEditing ? "modifier" : "créer"
        } des unités.`
      );
      setSubmitting(false);
      return;
    }

    try {
      // 1. Vérifier tous les champs obligatoires sont présents
      if (!values.name) {
        showFieldValidationError("name", "Veuillez remplir le champ 'Nom de l'unité'");
        setSubmitting(false);
        return;
      }
      if (!values.category_id) {
        showFieldValidationError("category_id", "Veuillez sélectionner une 'Catégorie'");
        setSubmitting(false);
        return;
      }
      if (!values.unit_id) {
        showFieldValidationError(
          "unit_id",
          "Veuillez sélectionner une 'Unité de mesure (Stock)'"
        );
        setSubmitting(false);
        return;
      }
      if (!values.warehouse) {
        showFieldValidationError("warehouse", "Veuillez sélectionner un 'Établissement'");
        setSubmitting(false);
        return;
      }

      const incompleteVariableCost = variableCosts.find(
        (item) =>
          !String(item.label || "").trim() ||
          !item.supplier_id ||
          !item.expense_category_id
      );
      if (incompleteVariableCost) {
        setActiveTab("2");
        message.error(
          "Chaque coût variable additionnel doit avoir un libellé, un fournisseur et une catégorie de dépense."
        );
        setSubmitting(false);
        return;
      }

      // 2. Vérifier qu'un entrepôt valide est sélectionné
      const selectedWarehouseData = warehouses.find(
        (w) => w.id === Number(values.warehouse)
      );

      if (!selectedWarehouseData) {
        showFieldValidationError("warehouse", "Établissement non valide");
        setSubmitting(false);
        return;
      }

      // 3. Vérifier les intrants
      const invalidMaterial = materials?.find(
        (material) =>
          !material.product_id ||
          !Number.isFinite(Number(material.quantity)) ||
          Number(material.quantity) <= 0
      );

      if (
        !materials ||
        materials.length === 0 ||
        !!invalidMaterial
      ) {
        message.error(
          materials?.length
            ? `Veuillez renseigner correctement chaque ${productionTerminology.materialSingular} (article et quantité > 0).`
            : `Veuillez ajouter au moins ${productionTerminology.materialIndefiniteSingular} valide`
        );
        focusInvalidMaterialInput();
        setSubmitting(false);
        return;
      }

      // 3bis. Garde-fou recalibrage : si des intrants "stock" restent calibres
      // pour une autre quantite produite, proposer un choix avant d'enregistrer.
      // On lit un eventuel override pose synchroniquement par le Modal de garde
      // (Recalculer/Conserver) pour ne pas dependre du timing de commit React.
      const effectiveMaterials = submitMaterialsOverrideRef.current ?? materials;
      submitMaterialsOverrideRef.current = null;
      const submitOutputQuantity = Number(values.output_quantity);
      const stillStaleMaterials = effectiveMaterials.filter((material) =>
        isStaleStock(material, submitOutputQuantity)
      );
      if (stillStaleMaterials.length > 0) {
        setSubmitting(false);
        setStaleGuardOpen(true);
        return;
      }

      // 4. Préparer les données, à la fois pour FormData et pour un envoi JSON standard
      const formData = new FormData();
      const jsonData = {
        product_type: "production", // Spécifier explicitement le type
      };

      // Ajout des données de base
      formData.append("name", values.name);
      jsonData.name = values.name;

      formData.append("description", values.description || "");
      jsonData.description = values.description || "";

      // Conversion explicite des valeurs pour le statut (utiliser 'active' ou 'inactive' au lieu de 1 ou 0)
      const statusValue = values.status ? "active" : "inactive";
      formData.append("status", statusValue);
      jsonData.status = statusValue;

      formData.append("company_id", selectedWarehouseData.company_id);
      jsonData.company_id = selectedWarehouseData.company_id;

      formData.append("warehouse_id", values.warehouse);
      jsonData.warehouse_id = parseInt(values.warehouse, 10);

      formData.append("category_id", values.category_id);
      jsonData.category_id = parseInt(values.category_id, 10);

      formData.append("slug", values.slug);
      jsonData.slug = values.slug;

      const productionKindValue =
        values.production_kind === PRODUCTION_KIND_SEMI_FINISHED
          ? PRODUCTION_KIND_SEMI_FINISHED
          : PRODUCTION_KIND_FINISHED;
      formData.append("production_kind", productionKindValue);
      jsonData.production_kind = productionKindValue;

      const barcodeSymbologyValue = normalizeBarcodeSymbology(
        values.barcode_symbology ?? form.getFieldValue("barcode_symbology")
      );
      if (!barcodeSymbologyValue) {
        showFieldValidationError(
          "barcode_symbology",
          "Veuillez sélectionner un type de code-barres."
        );
        setSubmitting(false);
        return;
      }
      formData.append("barcode_symbology", barcodeSymbologyValue);
      jsonData.barcode_symbology = barcodeSymbologyValue;

      const itemCodeValue = normalizeItemCodeForSymbology(
        values.item_code ?? form.getFieldValue("item_code") ?? currentBarcode,
        barcodeSymbologyValue
      );
      if (!itemCodeValue) {
        showFieldValidationError(
          "item_code",
          "Veuillez entrer ou générer un code article."
        );
        setSubmitting(false);
        return;
      }
      if (!isValidItemCodeForSymbology(itemCodeValue, barcodeSymbologyValue)) {
        const invalidCodeMessage =
          barcodeSymbologyValue === "EAN13"
            ? "Pour le format EAN13, le code doit contenir 12 ou 13 chiffres."
            : "Le code article contient des caractères incompatibles avec la symbologie sélectionnée.";
        showFieldValidationError("item_code", invalidCodeMessage);
        setSubmitting(false);
        return;
      }
      form.setFieldsValue({
        barcode_symbology: barcodeSymbologyValue,
        item_code: itemCodeValue,
      });
      formData.append("item_code", itemCodeValue);
      jsonData.item_code = itemCodeValue;

      formData.append("unit_id", values.unit_id);
      jsonData.unit_id = parseInt(values.unit_id, 10);

      const stockAlertValue = parseInt(values.stock_quantitiy_alert || 0, 10);
      formData.append("stock_quantitiy_alert", stockAlertValue);
      jsonData.stock_quantitiy_alert = stockAlertValue;

      const salesPriceValue = parseFloat(values.sales_price || 0);
      formData.append("sales_price", salesPriceValue);
      jsonData.sales_price = salesPriceValue;

      const rawTargetMarginRate = values.target_margin_rate;
      const targetMarginRateValue =
        rawTargetMarginRate === undefined ||
        rawTargetMarginRate === null ||
        rawTargetMarginRate === ""
          ? null
          : parseFloat(rawTargetMarginRate);

      if (
        targetMarginRateValue !== null &&
        (!Number.isFinite(targetMarginRateValue) ||
          targetMarginRateValue < 0 ||
          targetMarginRateValue >= 100)
      ) {
        showFieldValidationError(
          "target_margin_rate",
          "Le taux de marge cible doit être compris entre 0 et 99,99."
        );
        setSubmitting(false);
        return;
      }

      if (targetMarginRateValue !== null) {
        formData.append("target_margin_rate", targetMarginRateValue);
        jsonData.target_margin_rate = targetMarginRateValue;
      }

      // AJOUT: Gestion de la taxe (optionnel)
      if (values.tax_id) {
        formData.append("tax_id", values.tax_id);
        jsonData.tax_id = parseInt(values.tax_id, 10);
      }

      // S'assurer que output_quantity est un nombre
      const outputQuantity = parseFloat(values.output_quantity) || 1;
      formData.append("output_quantity", outputQuantity);
      jsonData.output_quantity = outputQuantity;

      // Conditionnement de production (optionnel) : label + quantite contenue.
      // Les deux doivent etre renseignes ensemble pour etre persistes.
      const packagingLabelRaw =
        typeof values.production_packaging_label === "string"
          ? values.production_packaging_label.trim()
          : "";
      const packagingContainedRaw = parseFloat(
        values.production_packaging_contained_quantity
      );
      const packagingIsValid =
        packagingLabelRaw.length > 0 &&
        Number.isFinite(packagingContainedRaw) &&
        packagingContainedRaw > 0;
      const packagingLabelToSend = packagingIsValid ? packagingLabelRaw : "";
      const packagingContainedToSend = packagingIsValid
        ? packagingContainedRaw
        : "";
      formData.append("production_packaging_label", packagingLabelToSend);
      formData.append(
        "production_packaging_contained_quantity",
        packagingContainedToSend
      );
      jsonData.production_packaging_label = packagingIsValid
        ? packagingLabelRaw
        : null;
      jsonData.production_packaging_contained_quantity = packagingIsValid
        ? packagingContainedRaw
        : null;

      formData.append("product_type", "production");

      // Recette "vendable sans production" : autorise la vente sans stock
      // (stock negatif), regularise ensuite en fin de journee. Defaut false.
      const sellableWithoutProductionValue = values.sellable_without_production
        ? "1"
        : "0";
      formData.append(
        "sellable_without_production",
        sellableWithoutProductionValue
      );
      jsonData.sellable_without_production =
        !!values.sellable_without_production;

      // Add user_id
      const userIdValue = user && user.id ? user.id : null;
      formData.append("user_id", userIdValue);
      jsonData.user_id = userIdValue;

      // Ajout de l'image si fournie (compression côté client pour la caméra
      // mobile : réduit le poids sous la limite Spaces 10 Mo, accélère l'upload).
      if (values.image && values.image[0] && values.image[0].originFileObj) {
        let imageToUpload = values.image[0].originFileObj;
        try {
          imageToUpload = await compressImage(imageToUpload, {
            maxSize: 1600,
            quality: 0.8,
          });
        } catch {
          imageToUpload = values.image[0].originFileObj;
        }
        formData.append("image", imageToUpload);
      }

      // Ajout des matières premières (depuis effectiveMaterials : prend en
      // compte un recalcul/une conservation valides par le garde-fou).
      const materialsJson = effectiveMaterials.map(({ product_id, quantity }) => ({
        product_id: parseInt(product_id, 10),
        quantity: parseFloat(quantity),
      }));

      // Vérifier qu'il y a des intrants valides
      if (
        materialsJson.length === 0 ||
        materialsJson.some(
          (item) =>
            Number.isNaN(item.product_id) ||
            !Number.isFinite(item.quantity) ||
            item.quantity <= 0
        )
      ) {
        message.error(
          `Veuillez renseigner correctement chaque ${productionTerminology.materialSingular} (article et quantité > 0).`
        );
        focusInvalidMaterialInput();
        setSubmitting(false);
        return;
      }

      const materialsJsonString = JSON.stringify(materialsJson);
      formData.append("materials", materialsJsonString);
      jsonData.materials = materialsJson;

      const materialUsageProfilesJson = materials.flatMap((material) =>
        getMaterialUsageProfiles(material).map((profile) => ({
          id: profile.id ?? undefined,
          material_product_id: parseInt(material.product_id, 10),
          label: String(profile.label || "").trim(),
          reference_output_quantity:
            parseFloat(profile.reference_output_quantity) || outputQuantity,
          reference_output_unit_id: values.unit_id
            ? parseInt(values.unit_id, 10)
            : null,
          material_quantity:
            parseFloat(profile.material_quantity) ||
            parseFloat(material.quantity) ||
            0,
          material_unit_id: material.material_unit_id
            ? parseInt(material.material_unit_id, 10)
            : profile.material_unit_id || null,
          loss_rate: parseFloat(profile.loss_rate) || null,
          yield_rate: parseFloat(profile.yield_rate) || null,
          notes: String(profile.notes || "").trim() || null,
          is_default: Boolean(profile.is_default),
        }))
      );

      formData.append(
        "material_usage_profiles",
        JSON.stringify(materialUsageProfilesJson)
      );
      jsonData.material_usage_profiles = materialUsageProfilesJson;

      const variableCostsJson = variableCosts
        .map((item) => ({
          label: String(item.label || "").trim(),
          cost_per_unit: parseFloat(item.cost_per_unit) || 0,
          supplier_id: Number(item.supplier_id) || null,
          expense_category_id: Number(item.expense_category_id) || null,
        }))
        .filter((item) => item.label.length > 0);

      formData.append("variable_costs", JSON.stringify(variableCostsJson));
      jsonData.variable_costs = variableCostsJson;

      // Liens materiel (amortissement -> cout de revient). Serialise comme
      // variable_costs (le backend parse la chaine JSON via parseArrayField).
      const unitEquipmentJson = equipmentLinks
        .map((item) => ({
          equipment_id: Number(item.equipment_id) || null,
          oeuvre_coefficient:
            item.oeuvre_coefficient === undefined ||
            item.oeuvre_coefficient === null
              ? 1
              : Number(item.oeuvre_coefficient),
        }))
        .filter((item) => item.equipment_id);

      formData.append("unit_equipment", JSON.stringify(unitEquipmentJson));
      jsonData.unit_equipment = unitEquipmentJson;

      // Considérer l'unité de production elle-même comme le produit de sortie
      let outputsJson = [];

      if (isEditing && id) {
        // En mode édition, utiliser l'ID existant comme produit de sortie
        outputsJson = [
          {
            product_id: parseInt(id, 10), // L'ID de l'unité de production elle-même
            quantity: outputQuantity, // Utiliser la valeur convertie
          },
        ];
      } else {
        // En mode création, utiliser la valeur spéciale "self_reference"
        outputsJson = [
          {
            product_id: "self_reference", // Valeur spéciale
            quantity: outputQuantity, // Utiliser la valeur convertie
          },
        ];
      }

      const outputsJsonString = JSON.stringify(outputsJson);
      formData.append("outputs", outputsJsonString);
      jsonData.outputs = outputsJson;


      let response;
      if (isEditing) {
        // En cas de mise à jour, utiliser FormData (avec l'image)
        response = await updateProductionUnit(id, formData);
        message.success(
          `${productionTerminology.unitSingularTitle} mise à jour avec succès`
        );
        navigate("/production/units");
      } else {
        try {
          // En cas de création, essayer d'abord avec FormData (pour l'image)
          response = await createProductionUnit(formData);
          message.success(
            `${productionTerminology.unitSingularTitle} créée avec succès`
          );
          navigate("/production/units");
        } catch (formDataError) {
          console.error("Erreur lors de l'envoi avec FormData:", formDataError);

          // Tenter d'extraire le message d'erreur spécifique
          if (handleProductionSubmitError(formDataError)) {
            return;
          } else {
            // Si ça échoue, essayer avec JSON standard (sans l'image)
            try {
              response = await axios.post(
                `${API_URL}/api/production/units`,
                jsonData,
                {
                  headers: {
                    "Content-Type": "application/json",
                  },
                }
              );
              message.success(
                `${productionTerminology.unitSingularTitle} créée avec succès (sans image)`
              );
              navigate("/production/units");
            } catch (jsonError) {
              console.error("Erreur détaillée:", jsonError);
              if (handleProductionSubmitError(jsonError)) {
                return;
              }
              const errorMessage =
                jsonError.response?.data?.error ||
                jsonError.response?.data?.details ||
                "Erreur lors de l'enregistrement";
              message.error(errorMessage);
            }
          }
        }
      }
    } catch (error) {
      console.error("Erreur complète:", error);
      if (handleProductionSubmitError(error)) {
        return;
      }
      const errorMessage =
        error.error ||
        error.details ||
        error.response?.data?.error ||
        error.response?.data?.details ||
        `Erreur lors de l'enregistrement de ${productionTerminology.unitSingular}`;
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Fonction pour récupérer les détails d'une unité
  const fetchUnitDetails = async (unitId) => {
    try {
      const unit = units.find((u) => u.id === unitId);
      return unit ? unit.short_name : "unité";
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des détails de l'unité:",
        error
      );
      return "unité";
    }
  };

  // Fonction pour mettre à jour l'affichage des unités dans le tableau lors du changement d'unité
  const handleUnitChange = (unitId) => {
    // Mettre à jour les matières premières existantes avec la nouvelle unité par défaut
    if (materials.length > 0) {
      const unit = units.find((u) => u.id === unitId);
      const defaultUnitName = unit ? unit.short_name : "unité";

      setMaterials((prevMaterials) =>
        prevMaterials.map((material) => ({
          ...material,
          // Mettre à jour seulement si l'unité n'est pas déjà définie pour ce matériau spécifique
          unit_short_name: material.unit_short_name || defaultUnitName,
        }))
      );
    }
  };

  // Définition des colonnes de la table des matières premières
  const assistantCompatibleMaterialsCount = materials.filter(
    (material) => getMaterialPackagingProfiles(material).length > 0
  ).length;
  const packagingDrivenMaterialsCount = materials.filter(
    (material) => material.recipe_entry_mode === "packaging"
  ).length;
  const usageProfileDrivenMaterialsCount = materials.filter(
    (material) => material.recipe_entry_mode === "usage_profile"
  ).length;

  const materialColumnsLegacy = [
    {
      title: productionTerminology.materialSingularTitle,
      dataIndex: "product_id",
      key: "product_id",
      render: (_, record) => (
        <Select
          value={record.product_id}
          onChange={(value) =>
            handleMaterialChange(record.key, "product_id", value)
          }
          style={{ width: "100%", minWidth: 180 }}
          placeholder={productionTerminology.selectMaterialPlaceholder}
          showSearch
          filterOption={false}
          onSearch={(value) => handleProductSearch(value, "input_materials")}
          loading={loadingProducts}
          notFoundContent={loadingProducts ? <Spin size="small" /> : null}
          dropdownStyle={{ minWidth: "300px" }}
        >
          {/* Si le produit actuel n'est pas dans la liste des options, l'afficher comme première option */}
          {record.product_id &&
            record.product_name &&
            !products.find((p) => areSameEntityId(p.id, record.product_id)) && (
              <Option
                key={`current-${record.product_id}`}
                value={record.product_id}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ flex: 1, fontStyle: "italic" }}>
                    {record.product_name} (actuel)
                  </span>
                </div>
              </Option>
            )}
          {products
            .filter(
              (product) =>
                product.product_type === "raw" ||
                (product.product_type === "production" &&
                  product.production_kind === PRODUCTION_KIND_SEMI_FINISHED)
            )
            .map((product) => {
              const profileCount = normalizePurchasePackagingProfiles(
                product.purchase_packaging_profiles
              ).length;

              return (
                <Option
                  key={product.id}
                  value={product.id}
                  disabled={materials.some(
                    (material) =>
                      material.key !== record.key &&
                      areSameEntityId(material.product_id, product.id)
                  )}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span style={{ flex: 1 }}>{product.name}</span>
                    {product.product_type === "production" && (
                      <Tag color="gold" style={{ marginLeft: 8 }}>
                        Semi-fini
                      </Tag>
                    )}
                    {profileCount > 0 && (
                      <Tag color="green" style={{ marginLeft: 8 }}>
                        {`${profileCount} conditionnement(s)`}
                      </Tag>
                    )}
                    {product.purchase_price && (
                      <Tag color="blue" style={{ marginLeft: 8 }}>
                        {formatNumber(product.purchase_price)} CFA
                      </Tag>
                    )}
                  </div>
                </Option>
              );
            })}
        </Select>
      ),
    },
    {
      title: productionTerminology.materialQuantityLabel,
      dataIndex: "quantity",
      key: "quantity",
      width: 150,
      align: "center",
      render: (text, record) => (
        <div>
          <InputNumber
            min={0.01}
            step={0.01}
            value={record.quantity}
            onChange={(value) =>
              handleMaterialChange(record.key, "quantity", value)
            }
            style={{ width: "100%" }}
            controls={true}
            addonAfter={record.unit_short_name || "unité"}
          />
          {renderStaleRowIndicator(record)}
        </div>
      ),
    },
    {
      title: "Prix Unitaire",
      dataIndex: "purchase_price",
      key: "purchase_price",
      align: "right",
      width: 130,
      responsive: ["sm"],
      render: (price, record) => {
        return price && price > 0 ? `${formatNumber(price)} CFA` : "-";
      },
    },
    {
      title: "Coût Total",
      key: "total_cost",
      align: "right",
      width: 140,
      responsive: ["sm"],
      render: (_, record) => {
        const total =
          record.quantity && record.purchase_price && record.purchase_price > 0
            ? record.quantity * record.purchase_price
            : 0;
        return total > 0 ? `${formatNumber(total)} CFA` : "-";
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 60,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Tooltip title={productionTerminology.removeMaterialAction}>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeMaterial(record.key)}
            size="small"
            style={{ borderRadius: "4px" }}
          />
        </Tooltip>
      ),
    },
  ];

  const renderMaterialAssistant = (record) => {
    const purchaseProfiles = getMaterialPackagingProfiles(record);
    const selectedPurchaseProfile = getSelectedPackagingProfile(record);
    const usageProfiles = getMaterialUsageProfiles(record);
    const selectedUsageProfile = getSelectedMaterialUsageProfile(record);
    const canUsePackaging =
      String(record.product_type || "").toLowerCase() === "raw" &&
      purchaseProfiles.length > 0;
    const outputQuantity = Number(form.getFieldValue("output_quantity")) || 1;

    if (!record.product_id) {
      return (
        <Text type="secondary">
          Sélectionnez d'abord {productionTerminology.materialIndefiniteSingular}.
        </Text>
      );
    }

    if (record.recipe_entry_mode === "usage_profile") {
      const calculatedQuantity = calculateUsageProfileMaterialQuantity(
        selectedUsageProfile,
        outputQuantity
      );

      if (usageProfiles.length === 0) {
        return (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Aucun profil. Utilisez le menu d'actions pour enregistrer un{" "}
            {productionTerminology.usageProfileSingular}.
          </Text>
        );
      }

      return (
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          <Select
            size="small"
            value={selectedUsageProfile?.id ?? selectedUsageProfile?.client_key}
            onChange={(value) =>
              handleMaterialUsageProfileChange(record.key, value)
            }
            style={{ width: "100%" }}
          >
            {usageProfiles.map((profile) => (
              <Option
                key={profile.id ?? profile.client_key}
                value={profile.id ?? profile.client_key}
              >
                {profile.label}
              </Option>
            ))}
          </Select>
          {selectedUsageProfile && calculatedQuantity !== null && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {`Calculé : ${formatRecipeQuantity(calculatedQuantity)} ${
                record.unit_short_name || "unité"
              }`}
            </Text>
          )}
        </Space>
      );
    }

    if (record.recipe_entry_mode === "packaging") {
      if (!canUsePackaging) {
        return (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Aucun conditionnement d'achat enregistré pour {productionTerminology.materialDemonstrativeSingular}.
          </Text>
        );
      }

      return (
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          <Select
            size="small"
            value={selectedPurchaseProfile?.id}
            onChange={(value) =>
              handleMaterialPackagingProfileChange(record.key, value)
            }
            style={{ width: "100%" }}
          >
            {purchaseProfiles.map((profile) => (
              <Option key={profile.id} value={profile.id}>
                {`${profile.label} - ${formatRecipeQuantity(
                  profile.contained_quantity
                )} ${record.unit_short_name || "unité"}`}
              </Option>
            ))}
          </Select>
          <InputNumber
            size="small"
            min={0.01}
            step={0.01}
            value={normalizePositiveNumber(record.package_count, 1)}
            onChange={(value) =>
              handleMaterialPackagingCountChange(record.key, value)
            }
            style={{ width: "100%" }}
            addonAfter="cond."
          />
        </Space>
      );
    }

    const ingredientSuggestions = Array.isArray(
      record.semantic_quantity_suggestions
    )
      ? record.semantic_quantity_suggestions
      : [];
    const matched =
      ingredientSuggestions.find(
        (s) =>
          String(s.unit || "").toLowerCase() ===
          String(record.unit_short_name || "").toLowerCase()
      ) || ingredientSuggestions[0];

    // Rafraichissement LOCAL de la suggestion a la quantite produite courante
    // (per_output_quantity x output), recalcul a l'affichage, zero appel API.
    const sugCur = Number(watchedOutputQuantity);
    const hasPerOutput = Number(matched?.per_output_quantity) > 0;
    const suggestedQty =
      hasPerOutput && sugCur > 0
        ? round2(Number(matched.per_output_quantity) * sugCur)
        : Number(matched?.quantity);

    if (matched && Number(suggestedQty) > 0) {
      // "Utiliser" pose une base de calibrage exploitable : ligne "engine" avec
      // coefficient fige quand per_output_quantity est connu (recalculable), ou
      // valeur absolue calibree a l'output courant sinon.
      const handleUseSuggestion = () =>
        setMaterials((prevMaterials) =>
          prevMaterials.map((item) =>
            item.key === record.key
              ? {
                  ...item,
                  quantity: suggestedQty,
                  quantity_source: hasPerOutput ? "engine" : "manual",
                  calibration_output_quantity:
                    sugCur > 0
                      ? sugCur
                      : item.calibration_output_quantity || null,
                  calibration_per_output: hasPerOutput
                    ? Number(matched.per_output_quantity)
                    : null,
                }
              : item
          )
        );

      return (
        <Tag
          color="success"
          style={{
            margin: 0,
            padding: "2px 8px",
            whiteSpace: "normal",
            borderRadius: 6,
          }}
        >
          <Space size={6}>
            <Text style={{ fontSize: 12 }}>
              {`Suggéré ${formatRecipeQuantity(suggestedQty)} ${
                matched.unit || record.unit_short_name || ""
              } - ${matched.support_count} rec.`}
            </Text>
            <Button
              size="small"
              type="link"
              style={{ padding: 0, height: "auto" }}
              onClick={handleUseSuggestion}
            >
              Utiliser
            </Button>
          </Space>
        </Tag>
      );
    }

    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        Saisie directe en unité de stock.
      </Text>
    );
  };

  // Indicateur par-ligne affiche sous la quantite quand l'intrant "stock" est
  // calibre pour une autre quantite produite. Present dans les colonnes
  // quantite des deux modes (assiste ET expert).
  const renderStaleRowIndicator = (record) => {
    if (!staleMaterials.some((material) => material.key === record.key)) {
      return null;
    }
    return (
      <div style={{ marginTop: 4 }}>
        <Space size={4} wrap>
          <Tag color="gold" style={{ margin: 0 }}>
            À recalculer
          </Tag>
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: "auto" }}
            onClick={() => recalcStaleMaterials([record.key])}
          >
            Recalculer
          </Button>
          <Tooltip title="Fige le nombre affiché pour la quantité produite actuelle ; il sera remis à l'échelle lors des productions futures.">
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: "auto" }}
              onClick={() => keepStaleMaterials([record.key])}
            >
              Conserver
            </Button>
          </Tooltip>
        </Space>
      </div>
    );
  };

  // Définition des colonnes de la table des produits finis (outputs)
  const materialColumns = recipeAssistantEnabled
    ? [
        materialColumnsLegacy[0],
        {
          title: "Mode",
          key: "recipe_entry_mode",
          width: 170,
          render: (_, record) => {
            const profiles = getMaterialPackagingProfiles(record);
            const canUsePackaging =
              String(record.product_type || "").toLowerCase() === "raw" &&
              profiles.length > 0;

            if (!record.product_id) {
              return (
                <Text type="secondary">
                  Choisissez {productionTerminology.materialDefiniteSingular}
                </Text>
              );
            }

            return (
              <Select
                size="small"
                value={record.recipe_entry_mode || "stock"}
                onChange={(value) =>
                  handleMaterialEntryModeChange(record.key, value)
                }
                style={{ width: "100%" }}
              >
                <Option value="stock">Qté directe</Option>
                <Option value="usage_profile">
                  {productionTerminology.usageProfileSingularTitle}
                </Option>
                {canUsePackaging && (
                  <Option value="packaging">Conditionnement d'achat</Option>
                )}
              </Select>
            );
          },
        },
        {
          title: "Aide de saisie",
          key: "recipe_assistant",
          width: 240,
          render: (_, record) => renderMaterialAssistant(record),
        },
        {
          ...materialColumnsLegacy[1],
          title: productionTerminology.materialQuantityLabel,
          width: 170,
          render: (text, record) => (
            <div>
              <InputNumber
                min={0.01}
                step={0.01}
                value={record.quantity}
                onChange={(value) =>
                  handleMaterialChange(record.key, "quantity", value)
                }
                style={{ width: "100%" }}
                controls={true}
                disabled={
                  record.recipe_entry_mode === "packaging" ||
                  (record.recipe_entry_mode === "usage_profile" &&
                    Boolean(getSelectedMaterialUsageProfile(record)))
                }
                addonAfter={record.unit_short_name || "unité"}
              />
              {renderStaleRowIndicator(record)}
            </div>
          ),
        },
        materialColumnsLegacy[2],
        materialColumnsLegacy[3],
        {
          title: "Actions",
          key: "actions",
          width: 72,
          align: "center",
          fixed: "right",
          render: (_, record) => {
            const suggested = getSuggestedMaterialUsageProfiles(record);
            const selectedUsageProfile =
              getSelectedMaterialUsageProfile(record);
            const items = [
              {
                key: "save",
                icon: <SaveOutlined />,
                label: `Enregistrer comme ${productionTerminology.usageProfileSingular}`,
                onClick: () => openUsageProfileModal(record.key),
              },
            ];

            if (suggested.length > 0) {
              items.push({
                key: "import",
                icon: <RollbackOutlined />,
                label: "Importer un profil existant",
                children: suggested.map((profile) => ({
                  key: `import-${profile.id ?? profile.client_key}`,
                  label: `${profile.label} - ${
                    profile.source_production_unit_name ||
                    `autre ${productionTerminology.configurationSingular}`
                  }`,
                  onClick: () =>
                    importSuggestedMaterialUsageProfile(
                      record.key,
                      profile.id ?? profile.client_key
                    ),
                })),
              });
            }

            if (
              record.recipe_entry_mode === "usage_profile" &&
              selectedUsageProfile
            ) {
              items.push({
                key: "detach",
                danger: true,
                label: "Détacher le profil",
                onClick: () => removeSelectedMaterialUsageProfile(record.key),
              });
            }

            items.push({ key: "divider", type: "divider" });
            items.push({
              key: "remove",
              danger: true,
              icon: <DeleteOutlined />,
              label: productionTerminology.removeMaterialAction,
              onClick: () => removeMaterial(record.key),
            });

            return (
              <RowActionsMenu
                items={items}
                ariaLabel={`Afficher les actions de ${productionTerminology.materialDefiniteSingular} ${
                  record.product_name || record.product_id || record.key
                }`}
              />
            );
          },
        },
      ]
    : materialColumnsLegacy;

  const variableCostColumns = [
    {
      title: "Libellé",
      dataIndex: "label",
      key: "label",
      render: (_, record) => (
        <Input
          aria-label="Libellé du coût variable"
          value={record.label}
          placeholder="Ex: Main d'oeuvre"
          onChange={(event) =>
            updateVariableCost(record.key, "label", event.target.value)
          }
        />
      ),
    },
    {
      title: "Coût / unité (CFA)",
      dataIndex: "cost_per_unit",
      key: "cost_per_unit",
      width: 145,
      render: (_, record) => (
        <InputNumber
          min={0}
          step={1}
          aria-label={`Coût unitaire : ${record.label || "nouvelle ligne"}`}
          value={record.cost_per_unit}
          onChange={(value) =>
            updateVariableCost(record.key, "cost_per_unit", value || 0)
          }
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Fournisseur *",
      dataIndex: "supplier_id",
      key: "supplier_id",
      width: 200,
      render: (_, record) => (
        <Select
          aria-label={`Fournisseur : ${record.label || "nouvelle ligne"}`}
          value={record.supplier_id || undefined}
          placeholder="Sélectionner un fournisseur"
          loading={loadingSuppliers}
          showSearch
          allowClear
          optionFilterProp="label"
          style={{ width: "100%" }}
          status={!record.supplier_id ? "error" : undefined}
          onChange={(value) =>
            updateVariableCost(record.key, "supplier_id", value || null)
          }
          options={suppliers.map((supplier) => ({
            value: supplier.id,
            label: supplier.name,
          }))}
        />
      ),
    },
    {
      title: "Catégorie de dépense *",
      dataIndex: "expense_category_id",
      key: "expense_category_id",
      width: 200,
      render: (_, record) => (
        <Select
          aria-label={`Catégorie : ${record.label || "nouvelle ligne"}`}
          value={record.expense_category_id || undefined}
          placeholder="Sélectionner une catégorie"
          showSearch
          allowClear
          optionFilterProp="label"
          style={{ width: "100%" }}
          status={!record.expense_category_id ? "error" : undefined}
          onChange={(value) =>
            updateVariableCost(
              record.key,
              "expense_category_id",
              value || null
            )
          }
          options={expenseCategories.map((category) => ({
            value: category.id,
            label: category.name,
          }))}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 58,
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          aria-label={`Supprimer le coût ${record.label || "variable"}`}
          onClick={() => removeVariableCost(record.key)}
          size="small"
          style={{ borderRadius: "4px" }}
        />
      ),
    },
  ];

  const formatEquipmentAmount = (value) =>
    `${Math.round(Number(value) || 0).toLocaleString("fr-FR")} CFA`;

  const equipmentColumns = [
    {
      title: "Materiel de production",
      dataIndex: "equipment_id",
      key: "equipment_id",
      render: (_, record) => (
        <Select
          value={record.equipment_id || undefined}
          placeholder="Selectionner un materiel"
          style={{ width: "100%" }}
          loading={loadingEquipment}
          showSearch
          optionFilterProp="label"
          onChange={(value) =>
            updateEquipmentLink(record.key, "equipment_id", value)
          }
          options={availableEquipment.map((equipment) => ({
            value: equipment.id,
            label:
              Number(equipment.depreciation_rate_per_unit) > 0
                ? `${equipment.name} (${formatEquipmentAmount(
                    equipment.depreciation_rate_per_unit
                  )}/u)`
                : equipment.name,
          }))}
        />
      ),
    },
    {
      title: "Coefficient (u. d'oeuvre / unite)",
      dataIndex: "oeuvre_coefficient",
      key: "oeuvre_coefficient",
      width: 210,
      render: (_, record) => (
        <InputNumber
          min={0}
          step={0.1}
          value={record.oeuvre_coefficient}
          onChange={(value) =>
            updateEquipmentLink(
              record.key,
              "oeuvre_coefficient",
              value === null || value === undefined ? 1 : value
            )
          }
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Amortissement / unite",
      key: "unit_depreciation",
      width: 160,
      render: (_, record) => {
        const rate = Number(record.depreciation_rate_per_unit) || 0;
        const coefficient =
          record.oeuvre_coefficient === undefined ||
          record.oeuvre_coefficient === null
            ? 1
            : Number(record.oeuvre_coefficient);
        return formatEquipmentAmount(rate * coefficient);
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeEquipmentLink(record.key)}
          size="small"
          style={{ borderRadius: "4px" }}
        />
      ),
    },
  ];

  // Fonction pour mettre à jour les prix manquants des matières premières
  const updateMissingPrices = async (materialsToCheck) => {
    const materialsWithoutPrices = materialsToCheck.filter(
      (material) => !material.purchase_price || material.purchase_price === 0
    );

    if (materialsWithoutPrices.length === 0) {
      return;
    }

    // Récupérer les prix pour chaque matière première sans prix
    for (const material of materialsWithoutPrices) {
      if (material.product_id && selectedWarehouse) {
        try {
          const response = await axios.get(
            `${API_URL}/api/produits/${material.product_id}`,
            {
              params: {
                warehouse_id: selectedWarehouse,
                company_id: selectedCompany,
                with_stock: true,
              },
            }
          );

          if (
            response.data &&
            (response.data.purchase_price || response.data.unit_short_name)
          ) {
            const normalizedProfiles = normalizePurchasePackagingProfiles(
              response.data.purchase_packaging_profiles
            );
            // Mettre à jour cette matière première spécifique
            setMaterials((prevMaterials) =>
              prevMaterials.map((item) => {
                if (
                  item.key === material.key &&
                  areSameEntityId(item.product_id, material.product_id)
                ) {
                  return {
                    ...item,
                    purchase_price: response.data.purchase_price || 0,
                    unit_name: response.data.unit_name || item.unit_name,
                    unit_short_name:
                      response.data.unit_short_name || item.unit_short_name,
                    product_type: response.data.product_type || item.product_type,
                    production_kind:
                      response.data.production_kind || item.production_kind,
                    purchase_packaging_profiles: normalizedProfiles,
                  };
                }
                return item;
              })
            );

            // Mettre à jour également la liste des produits
            setProducts((prevProducts) => {
              const existingIndex = prevProducts.findIndex((p) =>
                areSameEntityId(p.id, material.product_id)
              );
                if (existingIndex >= 0) {
                  const updatedProducts = [...prevProducts];
                  updatedProducts[existingIndex] = {
                    ...updatedProducts[existingIndex],
                    product_type: response.data.product_type,
                    production_kind: response.data.production_kind,
                    purchase_price: response.data.purchase_price,
                    unit_name: response.data.unit_name,
                    unit_short_name: response.data.unit_short_name,
                    purchase_packaging_profiles: normalizedProfiles,
                  };
                  return updatedProducts;
                }
              return prevProducts;
            });
          }
        } catch (error) {
          console.error(
            `Erreur lors de la récupération du prix pour ${material.product_name}:`,
            error
          );
        }
      }
    }
  };

  // Display-only reference estimate; submission and authoritative costing remain unchanged.
  const costEstimate = useMemo(() => estimateProductionUnit({
    outputQuantity: watchedOutputQuantity,
    materials,
    variableCosts,
    equipmentLinks: isProductionActivity ? equipmentLinks : [],
    equipment: availableEquipment,
    staleMaterials: hasStaleMaterials,
  }), [watchedOutputQuantity, materials, variableCosts, equipmentLinks, availableEquipment, isProductionActivity, hasStaleMaterials]);
  const outputUnitLabel = units.find((unit) => String(unit.id) === String(watchedUnit))?.short_name || "unité";
  const missingGeneralFields = [
    !watchedCategory && "catégorie",
    !watchedUnit && "unité de mesure",
  ].filter(Boolean);
  const saveAction = (
    <Button
      type="primary"
      icon={<SaveOutlined />}
      onClick={() => form.submit()}
      loading={submitting}
      disabled={loading || !hasPermission(isEditing
        ? "Gestion Commerciale.Approvisionnement.Production.Unites.edit"
        : "Gestion Commerciale.Approvisionnement.Production.Unites.create")}
    >
      {isEditing ? "Mettre à jour" : activeTab === "1" ? "Créer l’unité" : "Créer"}
    </Button>
  );
  const reviewEstimate = () => {
    if (!costEstimate.quantity) return focusProductionFormField("output_quantity");
    if (costEstimate.depreciationCost === null) return revealSection("equipment");
    revealSection(costEstimate.variableCost === null ? "costs" : "materials");
  };

  return (
    <div className={`production-unit-form-container production-workspace${!loading && activeTab === "2" ? " production-workspace--composition" : !loading && activeTab === "1" ? " production-workspace--general" : ""}`} style={workspaceStyle}>
      <div className="production-workspace__main">
        <header className="production-workspace__header">
          <div className="production-workspace__heading">
            <Tooltip title="Retour à la liste">
              <Button icon={<ArrowLeftOutlined />} aria-label="Retour à la liste"
                onClick={() => navigate("/production/units")} type="text" />
            </Tooltip>
            <div>
              <Text className="production-workspace__eyebrow">
                {productionTerminology.unitsTitle}
              </Text>
              <Title level={1}>{watchedRecipeName || productionTerminology.createUnitTitle}</Title>
              <Text className="production-workspace__subtitle">
                {activeTab === "1"
                  ? "Définissez l’identité et le classement de cette unité."
                  : <>Configurez vos quantités, vos {productionTerminology.materialPlural} et vos coûts.</>}
              </Text>
            </div>
          </div>
          {activeTab !== "1" && activeTab !== "2" && saveAction}
        </header>

        {loading ? (
          <div style={{ padding: "50px", textAlign: "center" }}>
            <Spin size="large" />
          </div>
        ) : (
          <>
            {productionAssistantMode && !isEditing && (
              <Alert
                showIcon
                type={materials.length > 0 ? "success" : "info"}
                message={
                  materials.length > 0
                    ? `${productionTerminology.configurationSingularTitle} en cours de configuration`
                    : "Assistant de configuration actif"
                }
                description={
                  <Space direction="vertical" size={8}>
                    <Text>
                      Définissez ici l'unité{" "}
                      {productionTerminology.costContext === "cuisine"
                        ? "cuisinée"
                        : "produite"}{" "}
                      et les quantités de{" "}
                      {productionTerminology.materialPlural}. Chaque quantité
                      est exprimée dans l'unité de stock de l'article choisi.
                    </Text>
                    <Text type="secondary">
                      L'assistant de saisie permet maintenant de partir
                      d'un conditionnement d'achat, puis de convertir ce format
                      vers l'unité de stock attendue dans la {productionTerminology.configurationSingular}.
                    </Text>
                    <Text type="secondary">
                      Le mode assisté de {productionTerminology.configurationSingular} est préactivé dans l'onglet
                      stock pour vous laisser saisir aussi les {productionTerminology.materialPlural} en
                      sacs, cartons, bidons ou autres conditionnements.
                    </Text>
                    <Space wrap>
                      <Button
                        type="default"
                        icon={<ShoppingOutlined />}
                        onClick={() => {
                          const params = new URLSearchParams({
                            assistant: "production-setup",
                            action: "create-raw-material",
                          });

                          if (selectedWarehouse) {
                            params.set("warehouse", String(selectedWarehouse));
                          }

                          navigate(`/gestion-produits/produits?${params.toString()}`);
                        }}
                      >
                        Créer des {productionTerminology.materialPlural}
                      </Button>
                      <Button
                        type="link"
                        onClick={() =>
                          navigate("/production/units?assistant=production-setup")
                        }
                      >
                        Retour à l'assistant
                      </Button>
                    </Space>
                  </Space>
                }
                style={{ margin: "16px 16px 0" }}
              />
            )}
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              onFinishFailed={handleProductionFormFinishFailed}
              scrollToFirstError={{ behavior: "smooth", block: "center" }}
              initialValues={{
                status: true,
                output_quantity: 1,
                stock_quantitiy_alert: 0,
                sales_price: 0,
                target_margin_rate: undefined,
                barcode_symbology: "CODE128",
                production_kind: PRODUCTION_KIND_FINISHED,
                sellable_without_production: false,
              }}
              className="production-workspace__form"
            >
            <Tabs activeKey={activeTab} onChange={setActiveTab}>
              <TabPane
                tab={<span className="flex items-center gap-xs"><InfoCircleOutlined /> Informations générales</span>}
                key="1" forceRender
              >
                <div className="production-workspace__general-grid">
                  <Card className="production-workspace__section production-workspace__general-fields"
                    title={<span><InfoCircleOutlined /> Informations de l’unité</span>}>
                    <Row gutter={[16, 0]}>
                      <Col span={24}>
                        <Form.Item
                          label={productionTerminology.unitNameLabel}
                          name="name"
                          rules={[
                            {
                              required: true,
                              message: "Veuillez saisir un nom",
                            },
                          ]}
                        >
                          <Input
                            placeholder={
                              productionTerminology.costContext === "cuisine"
                                ? "Ex: Sauce bolognaise"
                                : "Ex: Production de jus d'orange"
                            }
                            onChange={(e) => {
                              const nameVal = e.target.value;
                              if (nameVal) {
                                const slug = generateSlug(nameVal);
                                const code = generateArticleCode(
                                  nameVal,
                                  form.getFieldValue("barcode_symbology")
                                );
                                form.setFieldsValue({
                                  slug: slug,
                                  item_code: code,
                                });
                                setBarcodeGenerated(false);
                              } else {
                                // When name is cleared, clear dependent fields
                                form.setFieldsValue({
                                  slug: "",
                                  item_code: "",
                                });
                                setBarcodeGenerated(false);
                              }
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={[16, 0]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label="Établissement"
                          name="warehouse"
                          rules={[
                            {
                              required: true,
                              message: "Veuillez sélectionner un établissement",
                            },
                          ]}
                        >
                          <Select
                            placeholder="Sélectionner un établissement"
                            loading={!warehouses.length}
                            disabled={!selectedCompany}
                            showSearch
                            filterOption={(input, option) =>
                              getOptionText(option)
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                          >
                            {warehouses.map((wh) => (
                              <Option key={wh.id} value={wh.id}>
                                {wh.name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label="Catégorie"
                          name="category_id"
                          rules={[
                            {
                              required: true,
                              message: "Veuillez sélectionner une catégorie",
                            },
                          ]}
                        >
                          <Select
                            placeholder="Sélectionner une catégorie"
                            showSearch
                            allowClear
                            className="w-full"
                          >
                            {categories.map((category) => (
                              <Option key={category.id} value={category.id}>
                                {category.name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={[16, 0]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label="Unité de mesure (Stock)"
                          name="unit_id"
                          rules={[
                            {
                              required: true,
                              message: "Veuillez sélectionner une unité",
                            },
                          ]}
                          tooltip="Unité de mesure pour le stock. Seules les unités de l'entreprise sélectionnée sont disponibles."
                        >
                          <Select
                            placeholder={
                              selectedCompany
                                ? "Sélectionner une unité"
                                : "Veuillez d'abord sélectionner une entreprise"
                            }
                            showSearch
                            allowClear
                            disabled={!selectedCompany}
                            loading={!units.length && selectedCompany}
                            filterOption={(input, option) =>
                              getOptionText(option)
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            onSelect={(value) => handleUnitChange(value)}
                          >
                            {units.map((unit) => (
                              <Option key={unit.id} value={unit.id}>
                                {unit.name} ({unit.short_name})
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={`Type de ${productionTerminology.costContext}`}
                          name="production_kind"
                          rules={[{ required: true, message: "Veuillez selectionner un type" }]}
                          tooltip="Definit si l'unite est un produit fini ou semi-fini."
                          extra={<span className="production-workspace__general-kind-help">
                            Un produit semi-fini peut être utilisé dans d’autres {productionTerminology.processPlural}.
                          </span>}
                        >
                          <Radio.Group className="production-workspace__kind" aria-label={`Type de ${productionTerminology.costContext}`}>
                            <Radio value={PRODUCTION_KIND_FINISHED}>Produit {getProductionKindLabel(PRODUCTION_KIND_FINISHED).toLowerCase()}</Radio>
                            <Radio value={PRODUCTION_KIND_SEMI_FINISHED}>{getProductionKindLabel(PRODUCTION_KIND_SEMI_FINISHED)}</Radio>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item name="description" label="Description">
                      <TextArea rows={2} placeholder={`Décrivez cette ${productionTerminology.unitSingular} (facultatif)...`} />
                    </Form.Item>
                    <ProductionDisclosure sectionKey="technical" title="Détails techniques"
                      description="Identifiant généré automatiquement"
                      expanded={expandedSections.technical} onToggle={toggleSection}>
                      <Form.Item
                        label="Identifiant automatique"
                        name="slug"
                        rules={[
                          { required: true, message: "Le slug est requis" },
                        ]}
                      >
                        <Input
                          placeholder="slug-de-unite"
                          readOnly
                          addonAfter={
                            <Tooltip title="Identifiant unique pour les URL">
                              <InfoCircleOutlined />
                            </Tooltip>
                          }
                        />
                      </Form.Item>
                    </ProductionDisclosure>
                  </Card>
                  <aside className="production-workspace__general-panel" aria-label="Image et disponibilité">
                    <h2><PictureOutlined /> Image & disponibilité</h2>
                    <div className="production-workspace__general-panel-body">
                      <Form.Item label={productionTerminology.unitImageLabel}
                        name="image" valuePropName="fileList"
                        getValueFromEvent={(event) => Array.isArray(event) ? event : event?.fileList}
                        className="production-workspace__photo-field">
                        <ProductionUnitImageInput previewUrl={summaryImage} />
                      </Form.Item>
                      <div className="production-workspace__general-status">
                        <Form.Item
                          name="status"
                          label={productionTerminology.unitStatusLabel}
                          valuePropName="checked"
                          className="mb-0"
                        >
                          <Switch
                            checkedChildren="Actif"
                            unCheckedChildren="Inactif"
                          />
                        </Form.Item>
                        <Text className="production-workspace__general-status-help">
                          Indique si cette {productionTerminology.unitSingular} est active et peut être utilisée.
                        </Text>
                      </div>
                      {missingGeneralFields.length > 0 && <Alert type="warning" showIcon
                        className="production-workspace__general-hint"
                        message={`${missingGeneralFields.join(" et ").replace(/^./, (letter) => letter.toUpperCase())} à renseigner.`} />}
                      <Button block className="production-workspace__next" onClick={() => setActiveTab("2")}>
                        Composition et coûts <ArrowRightOutlined />
                      </Button>
                      <div className="production-workspace__summary-actions">
                        <Button onClick={() => navigate("/production/units")}>Annuler</Button>
                        {saveAction}
                      </div>
                    </div>
                  </aside>
                </div>
              </TabPane>

              <TabPane
                tab={<span className="flex items-center gap-xs"><SettingOutlined /> Composition et coûts</span>}
                key="2" forceRender
              >
                <Card className="production-workspace__section production-workspace__output" title={<span><ShoppingOutlined /> {productionTerminology.outputProduct} & quantité produite</span>}>
                  <Row gutter={[16, 0]} align="top">
                    <Col xs={24} md={15}>
                      <Form.Item label={`${productionTerminology.outputProduct} principal`}>
                        <Input readOnly value={watchedRecipeName || "À renseigner dans les informations générales"}
                          suffix={<Button type="link" size="small" onClick={() => focusProductionFormField("name")}>Modifier</Button>} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={9}>
                      <Form.Item
                        shouldUpdate={(prev, next) =>
                          prev.production_packaging_label !==
                            next.production_packaging_label ||
                          prev.production_packaging_contained_quantity !==
                            next.production_packaging_contained_quantity ||
                          prev.unit_id !== next.unit_id
                        }
                        noStyle
                      >
                        {({ getFieldValue }) => {
                          const packagingLabel = (
                            getFieldValue("production_packaging_label") || ""
                          )
                            .toString()
                            .trim();
                          const containedQty = Number(
                            getFieldValue(
                              "production_packaging_contained_quantity"
                            )
                          );
                          const currentUnitShort = getFieldValue("unit_id")
                            ? units.find(
                                (u) => u.id === getFieldValue("unit_id")
                              )?.short_name || "unités"
                            : "unités";
                          return (
                            <Form.Item
                              name="output_quantity"
                              label={productionTerminology.defaultOutputQuantityLabel}
                              tooltip={`Quantité de ${productionTerminology.outputProduct.toLowerCase()} obtenue par cycle de ${productionTerminology.costContext} de cette ${productionTerminology.unitSingular}. Si un conditionnement de ${productionTerminology.costContext} est défini, vous pouvez saisir soit en unités de stock (mode direct), soit en nombre de conditionnements.`}
                              rules={[
                                { required: true, type: "number", min: 0.01 },
                              ]}
                            >
                              <OutputQuantityInput
                                unitShortName={currentUnitShort}
                                packagingLabel={packagingLabel}
                                containedQuantity={containedQty}
                                mode={outputQuantityInputMode}
                                onModeChange={setOutputQuantityInputMode}
                                onUserChange={markOutputQuantityAsManuallyEdited}
                              />
                            </Form.Item>
                          );
                        }}
                      </Form.Item>
                    </Col>
                  </Row>
                  <ProductionDisclosure sectionKey="packaging" title={`Conditionnement de ${productionTerminology.costContext}`}
                    description={watchedProductionPackagingLabel || "Facultatif · Saisie directe en unités de stock"}
                    expanded={expandedSections.packaging} onToggle={toggleSection}>
                    <Row gutter={[16, 0]}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="production_packaging_label"
                        label={`Conditionnement de ${productionTerminology.costContext} (optionnel)`}
                        tooltip={`Libellé du conditionnement typique ${
                          productionTerminology.costContext === "cuisine"
                            ? "cuisiné"
                            : "produit"
                        } par cette ${productionTerminology.unitSingular} (ex : 'Marmite de 25 kg'). Laissez vide si vous ne voulez saisir qu'en unités de stock.`}
                        dependencies={[
                          "production_packaging_contained_quantity",
                        ]}
                        rules={[
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              const containedQuantity = Number(
                                getFieldValue(
                                  "production_packaging_contained_quantity"
                                )
                              );

                              if (
                                !Number.isFinite(containedQuantity) ||
                                containedQuantity <= 0 ||
                                (typeof value === "string" && value.trim())
                              ) {
                                return Promise.resolve();
                              }

                              return Promise.reject(
                                new Error(
                                  "Saisissez aussi le libellé du conditionnement."
                                )
                              );
                            },
                          }),
                        ]}
                      >
                        <Input placeholder="Ex: Marmite de 25 kg" maxLength={191} />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item
                        shouldUpdate={(prev, next) =>
                          prev.production_packaging_label !==
                            next.production_packaging_label ||
                          prev.unit_id !== next.unit_id
                        }
                        noStyle
                      >
                        {({ getFieldValue }) => {
                          const packagingLabel = (
                            getFieldValue("production_packaging_label") || ""
                          )
                            .toString()
                            .trim();
                          const currentUnitShort = getFieldValue("unit_id")
                            ? units.find(
                                (u) => u.id === getFieldValue("unit_id")
                              )?.short_name || "unités"
                            : "unités";
                          return (
                            <Form.Item
                              name="production_packaging_contained_quantity"
                              label={`Contenu par conditionnement (${currentUnitShort})`}
                              tooltip="Nombre d'unités de stock contenues dans 1 conditionnement (ex : 22 si 1 marmite de 25 kg produit 22 plats)."
                              rules={
                                packagingLabel
                                  ? [
                                      {
                                        required: true,
                                        type: "number",
                                        min: 0.0001,
                                        message:
                                          "Saisissez le nombre d'unités contenues dans 1 conditionnement.",
                                      },
                                    ]
                                  : []
                              }
                            >
                              <InputNumber
                                style={{ width: "100%" }}
                                placeholder="Ex: 22"
                                min={0}
                                step={0.01}
                                addonAfter={currentUnitShort}
                              />
                            </Form.Item>
                          );
                        }}
                      </Form.Item>
                    </Col>
                    </Row>
                  </ProductionDisclosure>
                </Card>

                <Card
                  id="production-section-materials"
                  className="production-workspace__section"
                  title={
                    <div style={{ fontSize: "14px", fontWeight: "500" }}>
                      <ShoppingOutlined style={{ marginRight: "8px" }} />
                      {productionTerminology.materialsTitle}
                      <span className="production-workspace__section-count">{materials.length} ligne(s)</span>
                    </div>
                  }
                  extra={
                    <div className="production-workspace__mode">
                      <span>Mode de saisie</span>
                      <Radio.Group size="small" optionType="button" buttonStyle="solid"
                        aria-label="Mode de saisie des matières"
                        value={recipeAssistantEnabled}
                        onChange={(event) => setRecipeAssistantEnabled(event.target.value)}
                        options={[{ label: "Assisté", value: true }, { label: "Expert", value: false }]} />
                    </div>
                  }
                >
                  {recipeAssistantEnabled && <span className="production-workspace__helper">
                    Quantités nécessaires pour {formatRecipeQuantity(watchedOutputQuantity || 0)} {outputUnitLabel}.
                    {recipeAssistantEnabled && ` Saisissez en conditionnements d'achat ou en unités de stock. ${assistantCompatibleMaterialsCount} compatibles · ${packagingDrivenMaterialsCount} par achat · ${usageProfileDrivenMaterialsCount} par ${productionTerminology.usageProfileSingular}.`}
                  </span>}
                  {recipeAssistantEnabled &&
                    !isEditing &&
                    (loadingRecipeSuggestions ||
                      recipeNameSuggestions.length > 0) && (
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message={productionTerminology.similarConfigurationsTitle}
                        description={
                          loadingRecipeSuggestions ? (
                            <Spin size="small" />
                          ) : (
                            <Space
                              direction="vertical"
                              size={8}
                              style={{ width: "100%" }}
                            >
                              {recipeNameSuggestions.map((suggestion) => (
                                <div
                                  key={suggestion.production_unit_id}
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: 8,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <span>
                                    <Tag
                                      color={
                                        suggestion.source_layer === "curated"
                                          ? "orange"
                                          : suggestion.source_layer === "pool"
                                          ? "purple"
                                          : "green"
                                      }
                                    >
                                      {suggestion.source_layer === "curated"
                                        ? "Référentiel"
                                        : suggestion.source_layer === "pool"
                                        ? "Communauté"
                                        : `Votre ${productionTerminology.configurationSingular}`}
                                    </Tag>{" "}
                                    <Text strong>{suggestion.recipe_name}</Text>{" "}
                                    <Tag color="blue">
                                      {suggestion.ingredients.length}{" "}
                                      {productionTerminology.materialPlural}
                                    </Tag>{" "}
                                    <Text type="secondary">
                                      {Math.round(
                                        (suggestion.confidence || 0) * 100
                                      )}
                                      % similaire
                                    </Text>
                                  </span>
                                  <Button
                                    size="small"
                                    type="primary"
                                    ghost
                                    onClick={() =>
                                      applyRecipeSuggestion(suggestion)
                                    }
                                  >
                                    Appliquer cette {productionTerminology.configurationSingular}
                                  </Button>
                                </div>
                              ))}
                            </Space>
                          )
                        }
                      />
                    )}
                  {recipeAssistantEnabled &&
                    !isEditing &&
                    (loadingMissingIngredients ||
                      missingIngredientSuggestions.length > 0) && (
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message={productionTerminology.frequentMaterialsTitle}
                        description={
                          loadingMissingIngredients ? (
                            <Spin size="small" />
                          ) : (
                            <Space
                              direction="vertical"
                              size={8}
                              style={{ width: "100%" }}
                            >
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                Ces suggestions apparaissent dans des {productionTerminology.configurationPlural} similaires, mais sont absentes de la vôtre.
                              </Text>
                              {missingIngredientSuggestions.map((suggestion) => (
                                <div
                                  key={suggestion.canonical}
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: 8,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <span>
                                    <Tag
                                      color={
                                        suggestion.source_layers?.includes(
                                          "intra"
                                        )
                                          ? "green"
                                          : suggestion.source_layers?.includes(
                                              "pool"
                                            )
                                          ? "purple"
                                          : "orange"
                                      }
                                    >
                                      {suggestion.source_layers?.includes(
                                        "intra"
                                      )
                                        ? `Vos ${productionTerminology.configurationPlural}`
                                        : suggestion.source_layers?.includes(
                                            "pool"
                                          )
                                        ? "Communauté"
                                        : "Référentiel"}
                                    </Tag>{" "}
                                    <Text strong>{suggestion.name}</Text>{" "}
                                    {Number(suggestion.quantity) > 0 && (
                                      <Text type="secondary">
                                        ≈{" "}
                                        {formatRecipeQuantity(
                                          suggestion.quantity
                                        )}{" "}
                                        {suggestion.unit || ""}
                                      </Text>
                                    )}{" "}
                                    <Tag color="blue">
                                      {suggestion.support_count} rec.
                                    </Tag>
                                  </span>
                                  <Button
                                    size="small"
                                    type="primary"
                                    ghost
                                    onClick={() =>
                                      applyMissingIngredient(suggestion)
                                    }
                                  >
                                    Ajouter
                                  </Button>
                                </div>
                              ))}
                            </Space>
                          )
                        }
                      />
                    )}
                  {hasStaleMaterials && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message={`${staleMaterials.length} ${
                        staleMaterials.length > 1
                          ? productionTerminology.materialPlural
                          : productionTerminology.materialSingular
                      } en saisie directe à recalibrer`}
                      description={
                        <Space
                          direction="vertical"
                          size={8}
                          style={{ width: "100%" }}
                        >
                          <Text type="secondary">
                            La quantité produite a changé : les quantités en
                            saisie directe ne sont pas ajustées automatiquement.
                            Recalculez-les proportionnellement ou conservez-les
                            telles quelles.
                          </Text>
                          <Space wrap>
                            <Button
                              type="primary"
                              ghost
                              size="small"
                              onClick={() => recalcStaleMaterials()}
                            >
                              Recalculer proportionnellement
                            </Button>
                            <Button
                              size="small"
                              onClick={() => keepStaleMaterials()}
                            >
                              Conserver les quantités
                            </Button>
                          </Space>
                        </Space>
                      }
                    />
                  )}
                  <div className="overflow-x-auto mb-md">
                    <Table
                      columns={materialColumns}
                      dataSource={materials}
                      rowKey="key"
                      pagination={false}
                      size="small"
                      bordered
                      scroll={{ x: recipeAssistantEnabled ? 1080 : 650 }}
                      locale={{ emptyText: productionTerminology.noMaterialAdded }}
                    />
                  </div>
                  <Button
                    type="dashed"
                    onClick={addMaterial}
                    block
                    icon={<PlusOutlined />}
                  >
                    {productionTerminology.addMaterialAction}
                  </Button>
                </Card>

                <Card
                  id="production-section-costs"
                  className="production-workspace__section"
                  title={
                    <div style={{ fontSize: "14px", fontWeight: "500" }}>
                      <TagOutlined style={{ marginRight: "8px" }} />
                      Coûts variables additionnels
                      <span className="production-workspace__section-count">{variableCosts.length} coût(s)</span>
                    </div>
                  }
                >
                  <span className="production-workspace__helper">
                    <InfoCircleOutlined /> Coût par {outputUnitLabel} produit. Fournisseur et catégorie obligatoires :
                    une dépense non payée est créée pour chaque ligne complète lors de la {productionTerminology.costContext}.
                    <Tooltip title="Les anciennes lignes incomplètes restent productibles, mais leur charge doit être régularisée depuis l'historique.">
                      <InfoCircleOutlined aria-label="À propos des anciennes lignes incomplètes" tabIndex={0} style={{ marginLeft: 6 }} />
                    </Tooltip>
                  </span>
                  <div className="overflow-x-auto mb-md">
                    <Table
                      columns={variableCostColumns}
                      dataSource={variableCosts}
                      rowKey="key"
                      pagination={false}
                      size="small"
                      bordered
                      scroll={{ x: 760 }}
                      locale={{ emptyText: "Aucun coût variable ajouté." }}
                    />
                  </div>
                  <Button
                    type="dashed"
                    onClick={addVariableCost}
                    block
                    icon={<PlusOutlined />}
                  >
                    Ajouter un coût variable
                  </Button>
                </Card>

                {isProductionActivity && (
                  <ProductionDisclosure sectionKey="equipment" title="Matériels & amortissement"
                    description={equipmentLinks.length ? `${equipmentLinks.length} matériel(s) associé(s)` : "Aucun matériel associé · Facultatif"}
                    expanded={expandedSections.equipment} onToggle={toggleSection}>
                    <span className="production-workspace__helper">
                      Associez les équipements utilisés. Leur amortissement est intégré au coût de revient,
                      selon les unités d'œuvre consommées par unité produite (coefficient 1 par défaut).
                    </span>
                    {availableEquipment.length === 0 && !loadingEquipment ? (
                      <Text type="secondary">
                        Aucun matériel disponible. Enregistrez vos matériels dans l’onglet « Matériels & amortissements » pour les associer ici.
                      </Text>
                    ) : (
                      <>
                        <div className="overflow-x-auto mb-md">
                          <Table
                            columns={equipmentColumns}
                            dataSource={equipmentLinks}
                            rowKey="key"
                            pagination={false}
                            size="small"
                            bordered
                            scroll={{ x: "max-content" }}
                            locale={{ emptyText: "Aucun matériel associé." }}
                          />
                        </div>
                        <Button
                          type="dashed"
                          onClick={addEquipmentLink}
                          block
                          icon={<PlusOutlined />}
                          className="w-full sm:w-auto"
                        >
                          Associer un matériel
                        </Button>
                      </>
                    )}
                  </ProductionDisclosure>
                )}

                <ProductionDisclosure sectionKey="sales" title="Vente & stock"
                  description={`Prix de vente : ${formatNumber(watchedSalesPrice || 0)} CFA · Alerte stock : ${formatRecipeQuantity(watchedStockAlert || 0)} ${outputUnitLabel} · Taxe et marge cible`}
                  expanded={expandedSections.sales} onToggle={toggleSection}>
                  <Row gutter={[16, 0]}>
                    <Col xs={24} sm={12} md={8}>
                      <Form.Item
                        name="stock_quantitiy_alert"
                        label={`Alerte stock (${productionTerminology.outputProduct})`}
                        tooltip={`Seuil d'alerte pour le réapprovisionnement du ${productionTerminology.outputProduct.toLowerCase()} de cette ${productionTerminology.unitSingular}.`}
                        rules={[{ required: true, type: "number", min: 0 }]}
                      >
                        <InputNumber
                          style={{ width: "100%" }}
                          placeholder="Ex: 10"
                          addonAfter={
                            form.getFieldValue("unit_id")
                              ? units.find(
                                  (u) => u.id === form.getFieldValue("unit_id")
                                )?.short_name || "unités"
                              : "unités"
                          }
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={8}>
                      <Form.Item
                        name="sales_price"
                        rules={[{ required: true, type: "number", min: 0 }]}
                        tooltip={`Prix de vente suggéré pour le ${productionTerminology.outputProduct.toLowerCase()} de cette ${productionTerminology.unitSingular}.`}
                        label={
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              width: "100%",
                              gap: 8,
                            }}
                          >
                            {`Prix de vente (${productionTerminology.outputProduct})`}
                            <Button
                              type="link"
                              size="small"
                              style={{
                                padding: 0,
                                height: "auto",
                                fontWeight: 600,
                              }}
                              onClick={handleSuggestSalesPrice}
                              loading={suggestingPrice}
                            >
                              Suggérer
                            </Button>
                          </span>
                        }
                      >
                        <InputNumber
                          style={{ width: "100%" }}
                          min={0}
                          step={100}
                          formatter={(value) =>
                            value ? `${formatNumber(value)} CFA` : ""
                          }
                          parser={(value) => {
                            const parsed = value.replace(/[^\d]/g, "");
                            return parsed ? Number(parsed) : "";
                          }}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={8}>
                      <Form.Item
                        name="target_margin_rate"
                        label="Taux de marge cible"
                        tooltip="Marge cible calculee sur le prix de vente: (prix de vente - cout de revient) / prix de vente."
                        rules={[{ type: "number", min: 0, max: 99.99 }]}
                      >
                        <InputNumber
                          style={{ width: "100%" }}
                          min={0}
                          max={99.99}
                          step={1}
                          placeholder="Ex: 30"
                          addonAfter="%"
                          formatter={(value) =>
                            value || value === 0 ? `${value}%` : ""
                          }
                          parser={(value) =>
                            value
                              ? value.replace("%", "").replace(",", ".")
                              : ""
                          }
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={8}>
                      <Form.Item
                        name="tax_id"
                        label={`Taxe (${productionTerminology.outputProduct})`}
                        tooltip={`Taxe applicable lors de la vente du ${productionTerminology.outputProduct.toLowerCase()}.`}
                      >
                        <Select
                          showSearch
                          allowClear
                          placeholder="Sélectionner une taxe (optionnel)"
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            getOptionText(option)
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          {taxes.map((tax) => (
                            <Option key={tax.id} value={tax.id}>
                              {tax.name} ({tax.rate}%)
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>

                    <Col xs={24}>
                      <Form.Item
                        name="sellable_without_production"
                        valuePropName="checked"
                        className="mb-0"
                        tooltip={`Permet de vendre cette ${productionTerminology.unitSingular} même sans stock produit. Le stock devient négatif puis devra être régularisé en fin de journée (onglet Régularisation), ce qui produira exactement la quantité vendue.`}
                        label="Vendable sans production préalable"
                      >
                        <Switch
                          checkedChildren="Oui"
                          unCheckedChildren="Non"
                        />
                      </Form.Item>
                      <Text type="secondary" className="text-xs">
                        Si activé, la vente est autorisée sans produire d'abord
                        (le stock peut devenir négatif). Pensez à régulariser en
                        fin de journée pour solder le stock.
                      </Text>
                    </Col>

                    {priceSuggestion && (
                      <Col xs={24} md={16}>
                        <Alert
                          type="info"
                          showIcon
                          style={{ marginTop: 4 }}
                          message={
                            <Space wrap size="small">
                              <Text>
                                Cout:{" "}
                                <Text strong>
                                  {formatNumber(priceSuggestion.unit_cost)} CFA
                                </Text>
                              </Text>
                              <Text>
                                Prix suggere:{" "}
                                <Text strong>
                                  {formatNumber(
                                    priceSuggestion.suggested_sales_price
                                  )}{" "}
                                  CFA
                                </Text>
                              </Text>
                              <Text>
                                Marge:{" "}
                                <Text strong>
                                  {formatNumber(
                                    priceSuggestion.expected_margin_amount
                                  )}{" "}
                                  CFA (
                                  {formatPercent(
                                    priceSuggestion.expected_margin_rate
                                  )}
                                  %)
                                </Text>
                              </Text>
                            </Space>
                          }
                        />
                      </Col>
                    )}
                  </Row>
                </ProductionDisclosure>
              </TabPane>

              <TabPane
                tab=<span className="flex items-center gap-xs">
                  <BarcodeOutlined /> Code-barres
                </span>
                key="4"
                forceRender
              >
                <Row gutter={[16, 24]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="item_code"
                      label="Code Article / SKU"
                      rules={[
                        {
                          required: true,
                          message: "Veuillez entrer ou générer un code article",
                        },
                      ]}
                      tooltip={`Le code unique pour identifier cet article/${productionTerminology.unitSingular}. Peut être utilisé pour le code-barres.`}
                    >
                      <Input
                        placeholder="Ex: UJP001"
                        onChange={() => setBarcodeGenerated(false)}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="barcode_symbology"
                      label="Type de Code-barres"
                      rules={[
                        {
                          required: true,
                          message:
                            "Veuillez sélectionner un type de code-barres",
                        },
                      ]}
                      initialValue="CODE128"
                    >
                      <Select
                        placeholder="Sélectionner le type"
                        className="w-full"
                        onChange={(value) => {
                          const normalizedType = normalizeBarcodeSymbology(value);
                          const currentCode = form.getFieldValue("item_code");
                          setBarcodeGenerated(false);

                          if (!currentCode) {
                            return;
                          }

                          const normalizedCode = normalizeItemCodeForSymbology(
                            currentCode,
                            normalizedType
                          );

                          form.setFieldsValue({ item_code: normalizedCode });
                          if (
                            isValidItemCodeForSymbology(
                              normalizedCode,
                              normalizedType
                            )
                          ) {
                            form.setFields([{ name: ["item_code"], errors: [] }]);
                          }
                        }}
                      >
                        <Option value="CODE128">CODE128</Option>
                        <Option value="EAN13">EAN-13</Option>
                        <Option value="CODE39">CODE39</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <div className="flex flex-col sm:flex-row gap-sm mt-md">
                  <Button
                    type={barcodeGenerated ? "default" : "primary"}
                    icon={<BarcodeOutlined />}
                    onClick={generateBarcode}
                    className="w-full sm:w-auto"
                  >
                    {barcodeGenerated
                      ? "Régénérer le Code-barres"
                      : "Générer le Code-barres"}
                  </Button>
                  <Form.Item shouldUpdate noStyle>
                    {() => (
                      <Button
                        type="primary"
                        icon={<PrinterOutlined />}
                        onClick={showBarcode}
                        disabled={!form.getFieldValue("item_code")}
                        className="w-full sm:w-auto"
                      >
                        Afficher/Imprimer le Code-barres
                      </Button>
                    )}
                  </Form.Item>
                </div>
              </TabPane>
            </Tabs>
            </Form>
          </>
        )}
      </div>
      {activeTab === "2" && !loading && (
        <ProductionUnitSummary
          name={watchedRecipeName}
          imageUrl={summaryImage}
          unit={outputUnitLabel}
          estimate={costEstimate}
          salesPrice={watchedSalesPrice}
          materialsTitle={productionTerminology.materialsTitle}
          costContext={productionTerminology.costContext}
          showEquipment={isProductionActivity}
          onEquipment={() => revealSection("equipment")}
          onReview={reviewEstimate}
          onCancel={() => navigate("/production/units")}
          saveAction={saveAction}
          updatedAt={form.getFieldValue("updated_at")}
        />
      )}

      <Modal
        title={productionTerminology.usageProfileSingularTitle}
        open={Boolean(usageProfileModal)}
        onCancel={closeUsageProfileModal}
        onOk={saveUsageProfileModal}
        okText="Enregistrer"
        cancelText="Annuler"
        destroyOnClose
      >
        {usageProfileModal && (
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <div>
              <Text strong>Libellé du profil</Text>
              <Input
                value={usageProfileModal.label}
                onChange={(event) =>
                  updateUsageProfileModal("label", event.target.value)
                }
                placeholder="Ex: Par fournée de 16 lots"
                style={{ marginTop: 4 }}
              />
            </div>
            <div>
              <Text strong>Sortie de référence</Text>
              <InputNumber
                min={0.01}
                step={0.01}
                value={usageProfileModal.reference_output_quantity}
                onChange={(value) =>
                  updateUsageProfileModal(
                    "reference_output_quantity",
                    value
                  )
                }
                addonAfter={getOutputUnitShortName()}
                style={{ width: "100%", marginTop: 4 }}
              />
            </div>
            <div>
              <Text strong>Quantité consommée</Text>
              <InputNumber
                min={0.01}
                step={0.01}
                value={usageProfileModal.material_quantity}
                onChange={(value) =>
                  updateUsageProfileModal("material_quantity", value)
                }
                addonAfter={
                  materials.find(
                    (item) => item.key === usageProfileModal.materialKey
                  )?.unit_short_name || "unité"
                }
                style={{ width: "100%", marginTop: 4 }}
              />
            </div>
            <Row gutter={12}>
              <Col xs={24} sm={12}>
                <Text strong>Perte (%)</Text>
                <InputNumber
                  min={0}
                  step={0.01}
                  value={usageProfileModal.loss_rate}
                  onChange={(value) =>
                    updateUsageProfileModal("loss_rate", value || 0)
                  }
                  addonAfter="%"
                  style={{ width: "100%", marginTop: 4 }}
                />
              </Col>
              <Col xs={24} sm={12}>
                <Text strong>Rendement (%)</Text>
                <InputNumber
                  min={0}
                  step={0.01}
                  value={usageProfileModal.yield_rate}
                  onChange={(value) =>
                    updateUsageProfileModal("yield_rate", value || 0)
                  }
                  addonAfter="%"
                  style={{ width: "100%", marginTop: 4 }}
                />
              </Col>
            </Row>
            <div>
              <Text strong>Note</Text>
              <Input
                value={usageProfileModal.notes}
                onChange={(event) =>
                  updateUsageProfileModal("notes", event.target.value)
                }
                placeholder="Ex: trempage, cuisson, parage..."
                maxLength={500}
                style={{ marginTop: 4 }}
              />
            </div>
            <Space align="center">
              <Switch
                checked={Boolean(usageProfileModal.is_default)}
                onChange={(checked) =>
                  updateUsageProfileModal("is_default", checked)
                }
              />
              <Text>
                Profil par défaut pour {productionTerminology.materialDemonstrativeSingular}
              </Text>
            </Space>
          </Space>
        )}
      </Modal>

      {/* Garde-fou recalibrage : recalculer / conserver avant d'enregistrer */}
      <Modal
        title={`Quantités de ${productionTerminology.materialPlural} à recalibrer`}
        open={staleGuardOpen}
        onCancel={() => setStaleGuardOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Text>
            {productionTerminology.staleMaterialsSentenceStart} pour une autre
            quantité produite. Que souhaitez-vous faire avant d'enregistrer ?
          </Text>
          <Space wrap>
            <Button
              type="primary"
              onClick={() => {
                const next = buildRecalcMaterials(
                  materials,
                  getCurrentOutputQuantity()
                );
                submitMaterialsOverrideRef.current = next;
                setMaterials(next);
                setStaleGuardOpen(false);
                form.submit();
              }}
            >
              Recalculer puis enregistrer
            </Button>
            <Button
              onClick={() => {
                const next = buildKeepMaterials(
                  materials,
                  getCurrentOutputQuantity()
                );
                submitMaterialsOverrideRef.current = next;
                setMaterials(next);
                setStaleGuardOpen(false);
                form.submit();
              }}
            >
              Conserver et enregistrer
            </Button>
            <Button type="text" onClick={() => setStaleGuardOpen(false)}>
              Annuler
            </Button>
          </Space>
        </Space>
      </Modal>

      {/* Modal pour afficher le code-barres */}
      <Modal
        title={
          <div className="flex items-center">
            <BarcodeOutlined className="mr-sm" />
            <span>Code-barres pour: {currentBarcode}</span>
          </div>
        }
        open={barcodeModalVisible}
        onCancel={() => setBarcodeModalVisible(false)}
        width="90%"
        style={{ maxWidth: 450, top: 20 }}
        footer={[
          <Button
            key="close"
            icon={<RollbackOutlined />}
            onClick={() => setBarcodeModalVisible(false)}
            className="w-full sm:w-auto mb-sm sm:mb-0"
          >
            Fermer
          </Button>,
          <Button
            key="print"
            type="primary"
            icon={<PrinterOutlined />}
            className="w-full sm:w-auto"
            onClick={() => {
              const barcodeElement =
                document.getElementById("barcode-print-area");
              if (barcodeElement) {
                html2canvas(barcodeElement, { scale: 2 }).then((canvas) => {
                  const imgData = canvas.toDataURL("image/png");
                  const pdf = new jsPDF({
                    orientation: "portrait",
                    unit: "mm",
                    format: [70, 40],
                  });
                  const pdfWidth = pdf.internal.pageSize.getWidth();
                  const pdfHeight = pdf.internal.pageSize.getHeight();
                  const imgProps = pdf.getImageProperties(imgData);
                  const imgWidth = pdfWidth * 0.8;
                  const imgHeight =
                    (imgProps.height * imgWidth) / imgProps.width;
                  const x = (pdfWidth - imgWidth) / 2;
                  const y =
                    (pdfHeight - imgHeight) / 2 > 0
                      ? (pdfHeight - imgHeight) / 2
                      : 0;

                  pdf.addImage(imgData, "PNG", x, y, imgWidth, imgHeight);
                  pdf.save(`barcode-${currentBarcode}.pdf`);
                });
              }
            }}
          >
            Imprimer en PDF
          </Button>,
        ]}
      >
        <div
          id="barcode-print-area"
          className="text-center p-lg bg-white border border-dashed border-gray-300 rounded-md"
        >
          {currentBarcode && (
            <div>
              <Barcode
                value={currentBarcode}
                format={form.getFieldValue("barcode_symbology") || "CODE128"}
                width={1.5}
                height={60}
                displayValue={true}
                fontSize={14}
              />
            </div>
          )}
          {!currentBarcode && (
            <Text type="secondary">
              Aucun code article défini pour générer un code-barres.
            </Text>
          )}
        </div>
      </Modal>

      {activeTab !== "2" && renderTimestamps()}
    </div>
  );
};

export default ProductionUnitForm;
